/* Atelier Alpino (design/skisises-luxe, CHANGES.md): the update's small behaviours over Prestige 10.7. A deferred
   module that uses only the theme's own vendor bundle (Motion One); every part checks for its element and does
   nothing elsewhere. Motion follows the theme's: transform, opacity and stroke only; reduced motion skips it. */
// Resolve the exact existing vendor URL; do not depend on Safari's import-map timing.
(async () => {
const vendorScript = document.querySelector('script[data-luxe-vendor]');
if (!vendorScript?.src) return;
const { animate, inView, stagger } = await import(vendorScript.src);

const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const phone = matchMedia('(max-width: 699px)');

/* ---------- filters on phones: a bottom sheet. luxe-collection.css anchors the drawer's content to the bottom;
   here the drawer's sideways slide is swapped for Prestige's own modal rise (x-modal, from below) while the
   viewport is a phone. The methods are looked up at every opening, so patching after the element exists works. */
customElements.whenDefined('facets-drawer').then(() => {
  const Facets = customElements.get('facets-drawer');
  const Modal = customElements.get('x-modal');
  if (!Facets || !Modal) return;
  ['createEnterAnimationControls', 'createLeaveAnimationControls'].forEach((name) => {
    const own = Facets.prototype[name];
    Facets.prototype[name] = function () {
      if (!phone.matches) return own.call(this);
      this.getShadowPartByName('content').style.marginInlineStart = '';
      return Modal.prototype[name].call(this);
    };
  });
});

/* ---------- the collection's type tiles and gender pills (snippets/luxe-type-filters.liquid, owner 05/10): Prestige
   draws them again with the grid at every filter, and the scripts of a redrawn section do not run, so their two
   behaviours listen on the document: "Mostra altri" on phones, and an icon that is missing gives way to the type's
   Italian icon, then to its name. */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.product-type-filters .filter-toggle-btn');
  if (!toggle) return;
  const box = toggle.closest('.product-type-filters');
  const open = box.dataset.expandedMobile !== 'true';
  box.dataset.expandedMobile = String(open);
  toggle.setAttribute('aria-expanded', String(open));
  const label = toggle.querySelector('.btn-label');
  if (label) label.textContent = open ? toggle.dataset.less : toggle.dataset.more;
});
const typeIconMissing = (img) => {
  const next = img.dataset.fallback;
  if (next) {
    delete img.dataset.fallback;
    img.src = next;
    return;
  }
  const fallback = document.createElement('span');
  fallback.className = 'filter-image--fallback';
  fallback.innerHTML = '<svg class="filter-image filter-image--fallback" viewBox="0 0 44 44" width="44" height="44" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M8 15 22 8l14 7v15l-14 7-14-7Z"/><path d="m8 15 14 7 14-7M22 22v15M15 11.5l14 7"/></svg>';
  img.replaceWith(fallback);
};
document.addEventListener('error', (event) => { if (event.target.matches?.('img.js-type-icon')) typeIconMissing(event.target); }, true);
document.querySelectorAll('img.js-type-icon').forEach((img) => { if (img.complete && !img.naturalWidth) typeIconMissing(img); });

/* ---------- the search field over a long filter list (snippets/facets.liquid, owner 12/10): typing keeps the values
   with a word that starts with the text, accents, case and punctuation aside ("bar" finds Barbour and Barbara Tani,
   "teryx" Arc'Teryx, "s" the sizes S and XS/S but not XS); the count in brackets is not searched. The field never
   reaches the filters' form: it has no name, its change stops before <facets-form> (which would reload the products)
   and Enter only closes the phone's keyboard. Prestige draws the filters again after each choice in the search page's
   sidebar, so the text is put back there; the drawer starts afresh at every opening. */
const facetWords = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const facetTexts = new Map();
const narrowFacet = (input) => {
  const box = input.closest('.luxe-facet-search');
  const query = facetWords(input.value);
  const joined = query.replaceAll(' ', '');
  let shown = 0;
  box.querySelectorAll('.checkbox-control').forEach((item) => {
    const words = facetWords((item.querySelector('label')?.textContent || '').replace(/\s*\(\d+\)\s*$/, ''));
    const match = !query || ` ${words}`.includes(` ${query}`) || words.replaceAll(' ', '').startsWith(joined);
    item.hidden = !match;
    if (match) shown += 1;
  });
  const empty = box.querySelector('.luxe-facet-search__empty');
  empty.textContent = shown ? '' : empty.dataset.text;
  box.querySelector('.luxe-facet-search__clear').hidden = !input.value;
  if (input.closest('facets-form')?.hasAttribute('update-on-change')) facetTexts.set(input.id, input.value);
};
const isFacetSearch = (target) => target.matches?.('.luxe-facet-search__input');
document.addEventListener('input', (event) => { if (isFacetSearch(event.target)) narrowFacet(event.target); });
document.addEventListener('change', (event) => { if (isFacetSearch(event.target)) event.stopPropagation(); }, true);
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || event.isComposing || !isFacetSearch(event.target)) return;
  event.preventDefault();
  event.target.blur();
});
document.addEventListener('click', (event) => {
  const clear = event.target.closest('.luxe-facet-search__clear');
  if (!clear) return;
  const input = clear.closest('.luxe-facet-search').querySelector('.luxe-facet-search__input');
  input.value = '';
  narrowFacet(input);
  input.focus();
});
// Prestige swaps the section's content right after this event, in the same task
document.addEventListener('theme:loading:end', () => setTimeout(() => facetTexts.forEach((text, id) => {
  const input = document.getElementById(id);
  if (!text || !input || input.value) return;
  input.value = text;
  narrowFacet(input);
})));

/* ---------- a size chosen in the filters shows only what is in stock in it (owner, 26/10): Shopify matches a size and
   «Solo prodotti disponibili» on the same variant, so the first size ticked in the list snippets/facets.liquid marks
   also ticks that switch, before <facets-form> reads the form, and taking off the last size takes it off again when
   the size turned it on; a switch the shopper set stays theirs. A chip that takes off the last size (a <facet-link>)
   loses the switch in its address too. Without the availability filter in Search & Discovery the form has no switch
   and nothing changes, and with no size chosen the collection stays as it is. A page that arrives with both (a reload,
   a shared link) counts as the size having turned it on. */
const STOCK = 'filter.v.availability';
let stockBySize = true;
document.addEventListener('change', (event) => {
  const input = event.target;
  const stock = input.form?.closest('facets-form') && input.form.elements[STOCK];
  if (!stock) return;
  if (input === stock) {
    stockBySize = false;
    return;
  }
  const sizes = input.closest('[data-luxe-size-filter]');
  if (!sizes || input.name !== sizes.dataset.luxeSizeFilter) return;
  const ticked = sizes.querySelectorAll('input:checked').length;
  if (input.checked && ticked === 1) {
    stockBySize = !stock.checked;
    stock.checked = true;
  } else if (!ticked && stockBySize) {
    stock.checked = false;
  }
}, true);
document.addEventListener('facet:update', (event) => {
  const url = event.detail?.url;
  const size = document.querySelector('[data-luxe-size-filter]')?.dataset.luxeSizeFilter;
  if (!stockBySize || !size || !url?.searchParams.has(STOCK) || url.searchParams.has(size)) return;
  if (new URLSearchParams(location.search).has(size)) url.searchParams.delete(STOCK);
}, true);

/* ---------- "Per chi lo riceve" from the home's gift band (?regalo=1): the gift card's own recipient checkbox,
   ticked and announced to its component so the recipient fields open. */
if (new URLSearchParams(location.search).get('regalo') === '1') {
  const tick = () => {
    const box = document.querySelector('gift-card-recipient input[type="checkbox"]');
    if (!box || box.checked) return;
    box.checked = true;
    box.dispatchEvent(new Event('change', { bubbles: true }));
  };
  customElements.whenDefined('gift-card-recipient').then(tick);
}

/* ---------- the search's pills (snippets/header-search.liquid, sections/predictive-search.liquid): a suggestion types
   its words into the field and Prestige's predictive search answers, as in the design; the pill's link to the search
   page stays for a click with a modifier (new tab) and without this script. */
document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-luxe-search-suggest]');
  if (!pill || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const input = pill.closest('header-search')?.querySelector('input[name="q"]');
  if (!input) return;
  event.preventDefault();
  input.value = pill.dataset.luxeSearchSuggest;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();
});

/* ---------- language and currency from the visitor's place (owner, 03/10: instead of the geolocation app's popup).
   On a visitor's first page, Shopify's own suggestions (/browsing_context_suggestions.json, by IP) give the country
   when the shop sells there; on a page in the shop's main language, the browser's languages give the language among
   the shop's (else Shopify's suggestion), while a page reached in another language keeps it. When they differ from the
   page, the theme's localization form is sent once and Shopify reloads the page in that market, currency and language.
   Once per visit (sessionStorage, owner 20/10: a visitor once seen in Italy and now in France gets France on the next
   visit, and so the bag's shipping to France), and never again once the visitor chose with a selector (the header's,
   the footer's or the bag's: LOCALIZATION_CHOSEN, localStorage), which then stays; never for crawlers or the theme
   editor; ?luxe-geo=1 runs it again (tests). The language rule is unchanged (owner, 20/10: the browser's language). */
const LOCALIZATION_CHOSEN = 'luxe:localization-chosen';
const markLocalizationChosen = () => { try { localStorage.setItem(LOCALIZATION_CHOSEN, '1'); } catch { /* the choice lasts this page */ } };
// the visitor's own choice: a localization form sent by a click (the automatic one below is sent with form.submit(),
// which fires no "submit")
document.addEventListener('submit', (event) => {
  if (event.target instanceof HTMLFormElement && /\/localization/.test(event.target.getAttribute('action') || '')) markLocalizationChosen();
}, true);
(() => {
  let data = null;
  try { data = JSON.parse(document.getElementById('luxe-localization')?.textContent || 'null'); } catch { return; }
  if (!data?.countries?.length) return;
  const KEY = 'luxe:localized';
  const forced = new URLSearchParams(location.search).get('luxe-geo') === '1';
  const crawler = /bot|crawl|spider|slurp|lighthouse|pagespeed|preview|facebookexternalhit|embedly|headless/i.test(navigator.userAgent) || navigator.webdriver;
  let seen = true;
  let chosen = false;
  try {
    chosen = localStorage.getItem(LOCALIZATION_CHOSEN) === '1';
    seen = sessionStorage.getItem(KEY) === '1';
    sessionStorage.setItem(KEY, '1');
  } catch { /* no storage: never */ }
  if (!forced && (seen || chosen || crawler || window.Shopify?.designMode)) return;
  fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((answer) => {
      const detected = answer?.detected_values?.country?.handle;
      const country = detected && data.countries.includes(detected) ? detected : data.country;
      let language = data.language;
      if (data.primary) {
        const shop = (tag) => data.languages.find((l) => l.toLowerCase() === String(tag || '').toLowerCase());
        const browser = (navigator.languages?.length ? navigator.languages : [navigator.language]).map((tag) => shop(tag) || shop(String(tag).split('-')[0])).find(Boolean);
        language = browser || shop(answer?.suggestions?.[0]?.parts?.language?.handle) || data.language;
      }
      if (country === data.country && language === data.language) return;
      const source = document.querySelector('form[action*="/localization"]');
      if (!source) return;
      const form = document.createElement('form');
      form.method = 'post';
      form.action = source.getAttribute('action');
      form.hidden = true;
      source.querySelectorAll('input[type="hidden"]').forEach((input) => { if (input.name !== 'country_code' && input.name !== 'locale_code') form.append(input.cloneNode()); });
      Object.entries({ country_code: country, locale_code: language }).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.append(input);
      });
      document.body.append(form);
      form.submit();
    })
    .catch(() => { /* the page stays as it is */ });
})();

/* ---------- the collections' infinite scroll (sections/main-collection.liquid, owner 03/10: the theme's instead of the
   app's): as the visitor nears the end of the grid, the next page is read from the same section and its cards (and
   looks) join the grid; the count and the next address follow, and the button does the same without scrolling. The
   page after is read as soon as one joins, so it is there before a quick thumb reaches the end. The page links stay
   in the page for search engines; with this script they are hidden. */
document.documentElement.classList.add('luxe-infinite');
const NEAR = 2400;
// the theme hides every card marked to rise in (theme.css) and its product list raises only the cards it holds when it
// first shows, so the cards that join later would stay hidden: these rise in as they come into view, as the first
// page's do (the same 20px, .2s and .05s apart), and show at once with reduced motion
const revealCards = (nodes) => {
  const cards = nodes.flatMap((node) => (node.matches?.('[reveal-on-scroll="true"]') ? [node] : [...node.querySelectorAll('[reveal-on-scroll="true"]')]));
  if (!cards.length) return;
  if (reduced.matches) { cards.forEach((card) => card.removeAttribute('reveal-on-scroll')); return; }
  let batch = [];
  inView(cards, ({ target }) => {
    if (!batch.length) {
      requestAnimationFrame(() => {
        animate(batch, { opacity: [0, 1], transform: ['translateY(20px)', 'translateY(0)'] }, { duration: .2, easing: 'ease-in-out', delay: stagger(.05) });
        batch = [];
      });
    }
    batch.push(target);
  });
};
class LuxeLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.luxe-load-more__button');
    this.button?.addEventListener('click', (event) => { event.preventDefault(); this.load(); });
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) this.load(); }, { rootMargin: `0px 0px ${NEAR}px 0px` });
      this.observer.observe(this);
    }
    // the next page is read as soon as the visitor starts scrolling (owner, 04/10: eight products are passed in a
    // moment), so it joins the grid at once when the end comes near
    const warm = () => { if (!this.ahead && this.dataset.next && !this.busy) this.ahead = { next: this.dataset.next, page: this.read(this.dataset.next) }; };
    addEventListener('scroll', warm, { passive: true, once: true });
    addEventListener('touchstart', warm, { passive: true, once: true });
    // and once the page has settled (Core Web Vitals, 12/10): a quick thumb on a slow phone reached the end before the
    // next page had arrived, and the cards that came then pushed the footer it was looking at (the journey recorder's
    // layout shifts on the collections, 17 of 20)
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(warm, { timeout: 4000 }) : setTimeout(warm, 2000));
    if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
  }

  disconnectedCallback() { this.observer?.disconnect(); }

  read(next) {
    const url = new URL(next, location.origin);
    url.searchParams.set('section_id', this.dataset.section);
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null))
      .catch(() => null);
  }

  async load() {
    const list = document.getElementById(this.dataset.list);
    if (this.busy || !this.dataset.next || !list) return;
    this.busy = true;
    this.classList.add('is-loading');
    const page = await (this.ahead?.next === this.dataset.next ? this.ahead.page : this.read(this.dataset.next));
    this.ahead = null;
    const more = page?.getElementById(this.dataset.list);
    if (more) {
      // the new cards go under the ones on screen and push the footer down: scroll anchoring would otherwise keep a
      // visitor who reached the footer (or this counter) where they are, with the new cards out of sight above
      const root = document.documentElement;
      root.style.overflowAnchor = 'none';
      const nodes = [...more.children].map((node) => document.importNode(node, true));
      list.append(...nodes);
      requestAnimationFrame(() => requestAnimationFrame(() => { root.style.overflowAnchor = ''; }));
      revealCards(nodes);
      const next = page.querySelector('luxe-load-more');
      if (next?.dataset.next) {
        this.dataset.next = next.dataset.next;
        this.querySelector('.luxe-load-more__count').textContent = next.querySelector('.luxe-load-more__count')?.textContent || '';
        if (this.button) this.button.href = next.dataset.next;
        this.ahead = { next: next.dataset.next, page: this.read(next.dataset.next) };
      } else {
        this.observer?.disconnect();
        this.remove();
      }
      try { window.jdgm?.customizeBadges?.(); } catch { /* the stars stay as they are */ }
      document.dispatchEvent(new CustomEvent('luxe:products-loaded', { detail: { list } }));
    }
    this.busy = false;
    this.classList.remove('is-loading');
    // still close to the end (a short page, a tall screen): the next one as well; a refused page waits for the
    // button or the next approach, and a visitor already past the grid is left where they are
    const top = this.getBoundingClientRect().top;
    if (more?.children.length && this.isConnected && top > 0 && top < innerHeight + NEAR) requestAnimationFrame(() => this.load());
  }
}
if (!customElements.get('luxe-load-more')) customElements.define('luxe-load-more', LuxeLoadMore);

/* ---------- "Nuovi arrivi" on the home page (sections/featured-collections.liquid with luxe_newest; 05/10, Core Web
   Vitals): a tab's rail is not in the page, so the server sends the home without its 24 cards. It is read from its
   collection sorted newest first (sections/luxe-arrivals-rail.liquid) and takes the place of its outlines. Reading
   starts after the page has loaded, so it never competes with the first screen, or sooner when the rail comes within
   a screen of view. Every tab is read at once, so changing tab shows its products at once too. */
class LuxeRail extends HTMLElement {
  static start() {
    if (LuxeRail.started) return;
    LuxeRail.started = true;
    document.querySelectorAll('luxe-rail').forEach((rail) => rail.load());
  }

  connectedCallback() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) LuxeRail.start(); }, { rootMargin: '0px 0px 100% 0px' });
      this.observer.observe(this);
    }
    const idle = () => (window.requestIdleCallback || ((callback) => setTimeout(callback, 200)))(() => LuxeRail.start(), { timeout: 2000 });
    if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
  }

  disconnectedCallback() { this.observer?.disconnect(); }

  async load() {
    this.observer?.disconnect();
    const html = await fetch(this.dataset.src, { headers: { Accept: 'text/html' } })
      .then((response) => (response.ok ? response.text() : null))
      .catch(() => null);
    const list = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('product-list') : null;
    const count = Number(this.dataset.count) || 12;
    list?.querySelectorAll('product-card').forEach((card, index) => { if (index >= count) card.remove(); });
    // nothing to show (an empty collection, a refused read): the outlines go and the "Vedi tutto" link below stays
    if (!list?.querySelector('product-card')) { this.hidden = true; return; }
    const rail = document.importNode(list, true);
    this.replaceWith(rail);
    try { window.jdgm?.customizeBadges?.(); } catch { /* the stars stay as they are */ }
    document.dispatchEvent(new CustomEvent('luxe:products-loaded', { detail: { list: rail } }));
  }
}
if (!customElements.get('luxe-rail')) customElements.define('luxe-rail', LuxeRail);

/* ---------- the mega menus' tiles (snippets/mega-menu.liquid; 05/10, Core Web Vitals): the packshot, photograph or
   service drawings of a mega menu are read after the page from sections/luxe-mega-tiles.liquid, so their product,
   collection and file look-ups (about 0.17 s of the server's render on a cache miss) no longer come before the first
   paint. Only where the menu shows (from 1000px), after load or as soon as the pointer or the focus enters the menu
   bar; the slot keeps the tile's size meanwhile. A slot left without a tile shows the block's own images, as before,
   or goes. */
const megaSlots = [...document.querySelectorAll('[data-luxe-mega-slot]')];
if (megaSlots.length) {
  const wide = matchMedia('(min-width: 1000px)');
  let tilesRead = false;
  const readTiles = async () => {
    if (tilesRead || !wide.matches) return;
    tilesRead = true;
    const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
    url.searchParams.set('section_id', 'luxe-mega-tiles');
    const html = await fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => (response.ok ? response.text() : null))
      .catch(() => null);
    const tiles = html ? new DOMParser().parseFromString(html, 'text/html') : null;
    megaSlots.forEach((slot) => {
      const tile = tiles?.querySelector(`[data-luxe-mega-tile="${CSS.escape(slot.dataset.luxeMegaSlot)}"] > .mega-menu__promo`);
      const fallback = slot.querySelector('template[data-luxe-mega-fallback]');
      if (tile) {
        slot.replaceWith(document.importNode(tile, true));
      } else if (fallback) {
        const promo = document.createElement('div');
        promo.className = 'mega-menu__promo';
        promo.append(fallback.content.cloneNode(true));
        slot.replaceWith(promo);
      } else {
        slot.remove();
      }
    });
  };
  const bar = document.querySelector('.header__primary-nav');
  bar?.addEventListener('pointerenter', readTiles, { once: true });
  bar?.addEventListener('focusin', readTiles, { once: true });
  wide.addEventListener?.('change', readTiles);
  const idle = () => (window.requestIdleCallback || ((callback) => setTimeout(callback, 200)))(readTiles, { timeout: 3000 });
  if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
}

/* ---------- the product page's sticky bar (Prestige's product-sticky-bar, sections/main-product.liquid; owner 03/10:
   the theme's instead of Zoorix): its sizes pick the same size in the page's picker, which redraws the bar with the
   new size; while the bar shows, html.sks-has-sticky-atc and --sks-sticky-stack-height lift the floating buttons
   (WhatsApp, the -10% bubble) above it, as they did for Zoorix (assets/newsletter-popup.js, layout/theme.liquid); on
   wide screens they join its row instead (owner, 29/10/2026). */
const pickStickySize = (host, value) => {
  const radio = document.querySelector(`input[type="radio"][form="${CSS.escape(host.dataset.form)}"][data-option-position="${CSS.escape(host.dataset.optionPosition)}"][value="${CSS.escape(value)}"]`);
  if (!radio) return;
  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
};

/* ---------- a sold-out size asks for the back-in-stock alert at once (owner, 04/10, as in the prototype): a tap on its
   button on the product page, or its line in the sticky bar's menu, opens the back-in-stock app's popup for that size,
   and the size chosen stays as it was, so the add button never turns into "Avvisami". The events are taken on the
   window, before the app's own listeners on the document would read the page's size again; when the popup closes the
   app reads it again (product:rerender), so its buttons follow the page. Without the app's popup the size is simply
   chosen, as before. */
/* the back-in-stock popup of a sold-out size (owner, 05/10): on top of the app's form, the same size in another colour
   of the model when one has it in stock (shown first, linking to that colour with the size chosen), otherwise "Avvisami
   in qualsiasi colore", which opens the theme's alert dialog for the model's size (assets/luxe-alerts.js). The model
   and its colourways come from the page's alert data (snippets/luxe-product-alerts.liquid); without it nothing is added. */
const BELL_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';
const bisExtras = (modal, variantId) => {
  modal.querySelector('.luxe-bis-extra')?.remove();
  const body = modal.querySelector('.bis-modal__body');
  const source = document.querySelector('script[id^="luxe-alert-product-"]');
  if (!body || !source) return;
  let data = {};
  try { data = JSON.parse(source.textContent || '{}') || {}; } catch { return; }
  const size = (Array.isArray(data.here) ? data.here : []).find((entry) => String(entry[1]) === String(variantId))?.[0];
  if (size == null) return;
  const label = String(size);
  const words = data.words || {};
  const extra = document.createElement('div');
  extra.className = 'luxe-bis-extra';
  const elsewhere = (Array.isArray(data.family) ? data.family : []).flatMap(([url, title, sizes]) => {
    const variant = (Array.isArray(sizes) ? sizes : []).find(([name, available]) => String(name) === label && available);
    return variant && url ? [{ url: String(url), title: String(title || ''), variant: variant[2] ? String(variant[2]) : '' }] : [];
  });
  if (elsewhere.length) {
    const line = document.createElement('p');
    line.className = 'luxe-bis-extra__elsewhere';
    line.append(String(words.elsewhere || '').replace('{size}', label), ' ');
    elsewhere.slice(0, 3).forEach((entry, index) => {
      if (index) line.append(' · ');
      const link = document.createElement('a');
      link.href = entry.variant ? `${entry.url}${entry.url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(entry.variant)}` : entry.url;
      link.textContent = entry.title || entry.url;
      line.append(link);
    });
    extra.append(line);
  } else {
    const dialog = document.getElementById(source.id.replace('luxe-alert-product-', 'luxe-alert-'));
    if (dialog && words.any) {
      // a call to action of the alerts script: it fills the dialog for this size, "In qualsiasi colore", and Prestige opens it
      const cta = document.createElement('div');
      cta.className = 'luxe-bis-extra__any';
      cta.dataset.luxeAlert = 'size';
      cta.dataset.luxeAlertSource = source.id;
      cta.dataset.luxeAlertSize = label;
      cta.dataset.luxeAlertScope = 'any';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'luxe-bis-extra__button';
      button.setAttribute('aria-controls', dialog.id);
      button.innerHTML = BELL_ICON;
      const text = document.createElement('span');
      text.textContent = words.any;
      button.append(text);
      button.addEventListener('click', () => { modal.hidden = true; });
      cta.append(button);
      extra.append(cta);
    }
  }
  if (extra.childNodes.length) body.prepend(extra);
};
const askBackInStock = (variantId) => {
  if (!variantId || typeof window.SkiSisesBackInStockOpen !== 'function') return false;
  try { window.SkiSisesBackInStockOpen({ variantId: String(variantId) }); } catch { return false; }
  const modal = document.querySelector('[data-back-in-stock-modal]');
  if (!modal || modal.hidden) return false;
  try { bisExtras(modal, variantId); } catch { /* the app's popup stays as it is */ }
  if (!modal.dataset.luxeWatch) {
    modal.dataset.luxeWatch = '1';
    new MutationObserver(() => { if (modal.hidden) document.dispatchEvent(new CustomEvent('product:rerender')); })
      .observe(modal, { attributes: true, attributeFilter: ['hidden'] });
  }
  return true;
};
// the storefront alerts' "In questo colore" (assets/luxe-alerts.js, owner 05/10) asks for this same popup for a size
// of the page's product: the event is cancelled when the popup opened
document.addEventListener('luxe:back-in-stock', (event) => { if (askBackInStock(event.detail?.variantId)) event.preventDefault(); });
addEventListener('click', (event) => {
  const label = event.target.closest?.('label.block-swatch.is-disabled, label.thumbnail-swatch.is-disabled');
  const input = label?.htmlFor ? document.getElementById(label.htmlFor) : null;
  if (!input?.dataset.luxeVariant || input.checked || !input.closest('.product-info')) return;
  event.preventDefault();
  event.stopPropagation();
  // in the quick buy: that product's popup (the page's own widget only knows the page's product)
  const quick = input.closest('quick-buy-modal')?.querySelector('luxe-quick-extras');
  // the size's own value: the label also carries its stock and "Avvisami" (journey test, 05/10)
  if (quick) { quickBackInStock(quick, input.dataset.luxeVariant, input.value); return; }
  if (askBackInStock(input.dataset.luxeVariant)) return;
  input.checked = true;
  input.dispatchEvent(new Event('change', { bubbles: true }));
}, true);
/* the bar's sizes, as in the design (owner, 04/10): its button opens them above the bar, each with its stock; a pick
   chooses that size on the page (the bar is drawn again with it), a sold-out one opens the back-in-stock popup for it
   and leaves the size as it was. Esc, the close button or a tap outside the bar close them. */
if (!customElements.get('luxe-sticky-sizes')) {
  customElements.define('luxe-sticky-sizes', class extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.toggle = this.querySelector('summary');
      if (!this.details || !this.toggle) return;
      this.details.addEventListener('toggle', () => {
        if (this.details.open) (this.querySelector('.luxe-sticky-size[aria-current="true"]') || this.querySelector('.luxe-sticky-size'))?.focus({ preventScroll: true });
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-luxe-sticky-close]')) { this.close(true); return; }
        const size = event.target.closest('a.luxe-sticky-size');
        if (!size) return;
        event.preventDefault();
        this.close(false);
        if (size.hasAttribute('data-luxe-soldout') && askBackInStock(size.dataset.luxeVariant)) return;
        if (size.getAttribute('aria-current') !== 'true') pickStickySize(this, size.dataset.value);
      });
      this.onKey = (event) => { if (event.key === 'Escape' && this.details.open) this.close(true); };
      this.onDown = (event) => { if (this.details.open && !event.target.closest?.('product-sticky-bar')) this.close(false); };
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('pointerdown', this.onDown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('pointerdown', this.onDown);
    }

    close(focusBack) {
      if (!this.details?.open) return;
      this.details.open = false;
      if (focusBack) this.toggle.focus({ preventScroll: true });
    }
  });
}
const STICKY_HOST = 'product-rerender[id$="-sticky-bar"]';
const stickyHost = document.querySelector(STICKY_HOST);
if (stickyHost) {
  const root = document.documentElement;
  const publish = () => {
    const host = document.querySelector(STICKY_HOST);
    const bar = host?.querySelector('product-sticky-bar');
    const shown = Boolean(bar?.classList.contains('is-visible'));
    const sizes = host?.querySelector('luxe-sticky-sizes');
    if (!shown) sizes?.close?.(false);
    // the sizes open (owner, 29/10/2026, «Vetro»): the phone's sheet covers the bar and the floating buttons step aside
    // (assets/luxe-product.css, assets/newsletter-popup.css); a class rather than :has(), which on the root restyles
    // the page at every node a script adds (Core Web Vitals, 12/10)
    root.classList.toggle('luxe-sticky-sizes-open', Boolean(shown && sizes?.querySelector('details[open]')));
    root.classList.toggle('sks-has-sticky-atc', shown);
    const before = root.style.getPropertyValue('--sks-sticky-stack-height');
    // the bar floats above the foot of the window: the stack is measured from its top edge
    if (shown) root.style.setProperty('--sks-sticky-stack-height', `${Math.max(0, Math.round(innerHeight - bar.getBoundingClientRect().top))}px`);
    else root.style.removeProperty('--sks-sticky-stack-height');
    // the -10% bubble lines up with WhatsApp by measuring it (assets/newsletter-popup.js): told when it moved
    if (root.style.getPropertyValue('--sks-sticky-stack-height') !== before) document.dispatchEvent(new CustomEvent('luxe:floating-stack'));
  };
  // only the bar's own class, its sizes opening and its redraws are watched, nothing the handler itself changes;
  // Prestige replaces the bar's <product-rerender> at every change of size, so the watch moves to the new one
  let watched = null;
  const inner = new MutationObserver(publish);
  const watch = () => {
    const host = document.querySelector(STICKY_HOST);
    if (!host || host === watched) return;
    watched = host;
    inner.disconnect();
    inner.observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'open'] });
    publish();
  };
  new MutationObserver(watch).observe(stickyHost.parentElement, { childList: true });
  watch();
  addEventListener('resize', publish, { passive: true });
  publish();
}

/* ---------- on the product page the floating buttons (WhatsApp, the -10% bar or bubble) step aside while the price,
   the sizes or the buy buttons pass the foot of the window, where they sit: they covered "Aggiungi al carrello" on a
   1440x900 desktop and the price on phones (journey test, 05/10). html.luxe-floating-aside fades them out
   (assets/luxe.css); they come back once those blocks leave the foot of the window, where the sticky bar takes over.
   An open -10% card or WhatsApp chat is never hidden. */
const mainInfo = document.querySelector('.shopify-section--main-product .product-info');
if (mainInfo && 'IntersectionObserver' in window) {
  const at = new Set();
  const foot = new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) at.add(entry.target); else at.delete(entry.target); });
    document.documentElement.classList.toggle('luxe-floating-aside', at.size > 0);
  }, { rootMargin: '-80% 0px 0px 0px' }); // the window's lowest fifth, where the floating buttons sit
  const watchBlocks = () => {
    foot.disconnect();
    at.clear();
    document.querySelectorAll('.shopify-section--main-product .product-info__block-item:is([data-block-type="price"], [data-block-type="variant-picker"], [data-block-type="buy-buttons"])')
      .forEach((block) => foot.observe(block));
  };
  watchBlocks();
  // a change of size can draw the blocks again
  document.addEventListener('product:rerender', watchBlocks);
}

/* ---------- the menu drawer comes after the page (sections/header.liquid, 15/10/2026): about 50 KB of every page's
   HTML and 100 pictures that only a tap on the menu button shows. The header leaves a [data-luxe-drawer-slot]; the drawer
   is asked for from /?view=luxe-drawer&section_id=<header> once the page is idle, or at the first tap on the button if
   that comes first, and put in its place with its Shadow DOM template, before the element, which needs it to upgrade.
   Prestige opens it from a click on [aria-controls="sidebar-menu"] delegated on <body>, so a tap that arrives first
   waits for the drawer and then opens it. ---------- */
{
  const slot = document.querySelector('[data-luxe-drawer-slot]');
  if (slot) {
    let pending = null;
    const load = () => pending || (pending = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}?view=luxe-drawer&section_id=${encodeURIComponent(slot.dataset.luxeDrawerSlot)}`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`drawer ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const template = doc.getElementById('header-sidebar-template');
      const drawer = doc.getElementById('sidebar-menu');
      if (!template || !drawer || !slot.isConnected) throw new Error('drawer missing');
      if (!document.getElementById('header-sidebar-template')) slot.before(document.importNode(template, true));
      const live = document.importNode(drawer, true);
      slot.replaceWith(live);
      return live;
    })().catch((error) => {
      pending = null;
      throw error;
    }));

    const isMenuButton = (target) => target.closest?.('[aria-controls="sidebar-menu"]');
    document.addEventListener('click', (event) => {
      if (document.getElementById('sidebar-menu') || !isMenuButton(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      // a failed request is forgotten: the next tap asks again
      load().then((drawer) => customElements.whenDefined('header-sidebar').then(() => drawer.show())).catch(() => {});
    }, true);
    // a finger on the button: the request leaves before the click
    document.addEventListener('pointerdown', (event) => {
      if (isMenuButton(event.target)) load().catch(() => {});
    }, { capture: true, passive: true });

    const idle = () => (window.requestIdleCallback ? requestIdleCallback(() => load().catch(() => {}), { timeout: 4000 }) : setTimeout(() => load().catch(() => {}), 2000));
    if (document.readyState === 'complete') idle();
    else addEventListener('load', idle, { once: true });
  }
}

/* ---------- the floating buttons (WhatsApp, a sticky cart bar) wait while the search or the menu is open:
   html.luxe-overlay-open (assets/luxe.css), set from the dialogs' own events as their open attribute comes and goes.
   It replaces a body:has(.header-search[open], header-sidebar[open]) rule: a :has() on <body> made Chrome restyle the page
   after every node any script added anywhere (Core Web Vitals, 12/10). The events do not bubble: captured. */
{
  const OVERLAYS = '.header-search, header-sidebar';
  const sync = () => document.documentElement.classList.toggle('luxe-overlay-open', Boolean(document.querySelector('.header-search[open], header-sidebar[open]')));
  ['dialog:before-show', 'dialog:before-hide'].forEach((type) => {
    document.addEventListener(type, (event) => { if (event.target.matches?.(OVERLAYS)) sync(); }, true);
  });
}

/* ---------- Judge.me's store rating (its "all reviews text" app block, footer) is a link to javascript:void(0), which search
   engines cannot follow (Lighthouse, 12/10): it leads to the store's reviews on Judge.me, like the line under it. */
{
  const fixRatingLinks = () => document.querySelectorAll('.jdgm-all-reviews-text a[href^="javascript"]').forEach((link) => {
    const reviews = link.closest('.jdgm-all-reviews-text')?.querySelector('a[href*="judge.me/reviews"]');
    link.href = reviews?.href || 'https://app.judge.me/reviews/stores/skisises.com';
    link.target = '_blank';
    link.rel = 'noopener';
  });
  fixRatingLinks();
  addEventListener('load', fixRatingLinks, { once: true });
}

/* ---------- phones: a tap on the header's search bar opens the search ready to type (journey test, 05/10). Prestige's
   dialogs move the focus only on devices that hover, so the keyboard needed a second tap on the field; focusing during
   the tap keeps it a user gesture, which iOS needs to raise the keyboard. The dialog has opened by now: Prestige opens
   it from the body, before the event reaches the document. ---------- */
document.addEventListener('click', (event) => {
  const bar = event.target.closest?.('a.luxe-searchbar[aria-controls]');
  if (!bar) return;
  document.getElementById(bar.getAttribute('aria-controls'))?.querySelector('input[name="q"]')?.focus({ preventScroll: true });
});

/* ---------- the bag's "Completa con" (sections/luxe-cart-suggest.liquid, owner 03/10): when the bag opens, what goes
   with the bag's products («Completa il kit», owner 26/10: the CRM's mode `complements`, their looks and the
   categories that go with them, never another jacket after a jacket; visitor id only with tracking consent, as
   assets/for-you.js), drawn as the theme's horizontal cards. No complement known: Shopify's complementary products
   for the bag's first product (Search & Discovery), and without those its related products, as before (a product the
   nightly has not reached yet, e.g. the first day). The control group (an answer of popular products)
   and an answer that does not come in 3 s get Shopify's related products, as before. Asked only for an open bag, once
   per set of products; the drawer draws its content again after every change, so the list is put back from memory. */
const suggestState = { key: null, html: '' };
const withTimeout = (promise, ms) => Promise.race([promise, new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), ms))]);
const suggestList = (html) => new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-luxe-cart-suggest-list]');
const shopRoot = () => window.Shopify?.routes?.root || '/';
// what goes with these products, from the CRM (mode complements, the products themselves excluded), for the bag and
// the product page: the visitor id only with tracking consent, as assets/for-you.js
const askKit = (ids) => {
  const privacy = window.Shopify?.customerPrivacy;
  const allowed = (name) => { try { return Boolean(privacy?.[name]?.()); } catch { return false; } };
  const a = allowed('analyticsProcessingAllowed');
  const m = allowed('marketingAllowed');
  const params = new URLSearchParams({ limit: '8', a: a ? '1' : '0', m: m ? '1' : '0', exclude: ids.slice(0, 20).join(','), mode: 'complements' });
  const visitor = (a || m) ? document.cookie.match(/(?:^|; )product_intent_visitor_id=([^;]*)/)?.[1] : '';
  if (visitor) params.set('v', decodeURIComponent(visitor));
  // «Le mie taglie» (assets/my-sizes.js): the shopper's own choice
  try { const mine = JSON.parse(localStorage.getItem('sks:my-sizes') || 'null')?.sizes; if (Array.isArray(mine) && mine.length) params.set('sizes', mine.slice(0, 8).join('|')); } catch { /* private mode */ }
  return withTimeout(fetch(`/apps/wishlist/for-you?${params}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)), 3000);
};
async function suggestFor(ids) {
  const key = ids.slice().sort().join(',');
  if (key === suggestState.key) return suggestState.html;
  let html = '';
  try {
    const answer = await askKit(ids);
    // "popular" is one list for everyone (no consent, no history): Shopify's picks for the bag's product fit better
    // (journey test, 05/10: €990 lodens next to a €25 hat)
    const items = answer?.source === 'popular' ? [] : (answer?.items || []);
    const query = items.map((item) => `id:${item.productId}`).join(' OR ');
    if (query) html = await fetch(`${shopRoot()}search?type=product&q=${encodeURIComponent(query)}&section_id=luxe-cart-suggest`).then((r) => (r.ok ? r.text() : ''));
    // the kit had nothing for this bag: Shopify's complementary products first
    else if (answer?.success && answer.group === 'treated') {
      html = await fetch(`${shopRoot()}recommendations/products?product_id=${ids[0]}&limit=8&intent=complementary&section_id=luxe-cart-suggest`).then((r) => (r.ok ? r.text() : ''));
    }
  } catch { /* the CRM is optional */ }
  if (!suggestList(html)?.children.length) {
    try { html = await fetch(`${shopRoot()}recommendations/products?product_id=${ids[0]}&limit=8&section_id=luxe-cart-suggest`).then((r) => (r.ok ? r.text() : '')); } catch { html = ''; }
  }
  suggestState.key = key;
  suggestState.html = html;
  return html;
}
// each refresh has its number: an older one still putting its list back stops, so a product just added (from these
// cards too) never comes back in the list it left
let suggestGen = 0;
const fillSuggest = (html, gen, tries = 14) => {
  if (gen !== suggestGen) return;
  const box = document.querySelector('#cart-drawer [data-luxe-cart-suggest]');
  const target = box?.querySelector('[data-luxe-cart-suggest-target]');
  const list = suggestList(html);
  if (target && target.dataset.luxeGen !== String(gen) && list?.children.length) {
    list.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      try { const url = new URL(link.getAttribute('href'), location.origin); url.searchParams.set('sks_rec', 'cart_suggest'); link.setAttribute('href', url.pathname + url.search); } catch { /* keep it */ }
    });
    target.replaceChildren(document.importNode(list, true));
    target.dataset.luxeGen = String(gen);
    box.hidden = false;
  }
  if (tries > 0) setTimeout(() => fillSuggest(html, gen, tries - 1), 150);
};
async function refreshSuggest(cart) {
  if (!document.getElementById('cart-drawer')?.hasAttribute('open')) return;
  const gen = ++suggestGen;
  try {
    const data = cart || await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const ids = (data.items || []).map((item) => String(item.product_id));
    if (ids.length) fillSuggest(await suggestFor(ids), gen);
  } catch { /* the bag works without it */ }
}
// heard on the document: the look pages put a new drawer in place of the old one (assets/outfit-page.js), and a
// listener on the old one went with it (journey test, 05/10: no suggestions and raw dates after "Aggiungi tutto")
document.addEventListener('dialog:after-show', (event) => {
  if (event.target?.id !== 'cart-drawer') return;
  localDays(event.target);
  refreshSuggest();
}, true);
document.addEventListener('cart:change', (event) => refreshSuggest(event.detail?.cart));

/* ---------- «Completa con» on the product page (snippets/complementary-products.liquid; personalization plan, wave 2,
   owner 28/10/2026): Search & Discovery's complementary products come first; when it has none for the page's product,
   Prestige's product-recommendations leaves the page and the section beside it shows what goes with the product from
   the CRM, as the bag's (askKit), drawn as the bag's horizontal cards (sections/luxe-cart-suggest.liquid). Asked once
   the page has loaded, or 4 s after this script ran at the latest (a third-party file can hold the load event for a
   minute), when the browser is idle, so it never competes with the first screen; a product changed in place (another
   colour, Prestige's product:rerender) has its new section asked too. The control group, a product without
   complements, an error or an answer later than 3 s leave the section hidden, as the page was; so does Search &
   Discovery still loading after 8 s. Product links carry sks_rec=pdp_kit. The cards' quick buy windows take ids of
   their own: the bag draws the same section, and two windows with one id opened together. */
async function pdpKitFor(id) {
  const answer = await askKit([id]);
  if (answer?.group !== 'treated' || answer.source !== 'complement' || !answer.items?.length) return '';
  const query = answer.items.map((item) => `id:${item.productId}`).join(' OR ');
  return fetch(`${shopRoot()}search?type=product&q=${encodeURIComponent(query)}&section_id=luxe-cart-suggest`).then((r) => (r.ok ? r.text() : ''));
}
// Search & Discovery's answer for the block: 'none' once its element has left the page, 'shown' once it shows
const shopifyComplementary = async (kit, tries = 40) => {
  const theirs = kit.parentElement?.querySelector('product-recommendations[intent="complementary"]');
  if (!theirs?.isConnected) return 'none';
  if (!theirs.hidden) return 'shown';
  if (tries <= 0) return 'pending';
  await new Promise((resolve) => setTimeout(resolve, 200));
  return shopifyComplementary(kit, tries - 1);
};
async function fillPdpKit(kit) {
  let html = '';
  try { html = await pdpKitFor(kit.dataset.product); } catch { return; }
  const list = suggestList(html);
  if (!list?.children.length || await shopifyComplementary(kit) !== 'none') return;
  list.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    try { const url = new URL(link.getAttribute('href'), location.origin); url.searchParams.set('sks_rec', 'pdp_kit'); link.setAttribute('href', url.pathname + url.search); } catch { /* keep it */ }
  });
  list.querySelectorAll('quick-buy-modal[id]').forEach((modal) => {
    const id = `pdp-kit-${modal.id}`;
    list.querySelectorAll(`[aria-controls="${CSS.escape(modal.id)}"]`).forEach((button) => button.setAttribute('aria-controls', id));
    modal.id = id;
  });
  kit.querySelector('[data-luxe-pdp-kit-target]')?.replaceChildren(document.importNode(list, true));
  kit.hidden = false;
}
const pdpKitsAsked = new WeakSet();
const askPdpKits = () => document.querySelectorAll('[data-luxe-pdp-kit]').forEach((kit) => {
  if (pdpKitsAsked.has(kit)) return;
  pdpKitsAsked.add(kit);
  fillPdpKit(kit);
});
if (document.querySelector('[data-luxe-pdp-kit]')) {
  const idle = () => (window.requestIdleCallback || ((callback) => setTimeout(callback, 200)))(askPdpKits, { timeout: 2000 });
  if (document.readyState === 'complete') idle();
  else {
    addEventListener('load', idle, { once: true });
    setTimeout(idle, 4000);
  }
}
// heard while it travels down to the product's form: Prestige sends it there, and it may not bubble
document.addEventListener('product:rerender', () => setTimeout(askPdpKits, 0), true);

/* ---------- the bag's discount code (snippets/luxe-cart-discount.liquid, owner 19/10): the codes go to the cart with
   the Ajax API (/cart/update.js, "discount": the whole list each time) and the sections the theme bundles, then the
   theme's "cart:change" draws the bag again, as after a quantity change; the cart page loads again, as Prestige does
   there. Every code the cart holds is sent back, the ones it does not apply yet too (a minimum not reached). A code
   just typed that the cart refuses (unknown, expired, not for these products) is taken out again at once and the
   field says so; one it accepts that the bag does not list (shipping) is said to show at checkout. What happened
   waits in memory (on the cart page, in the session) for the field drawn again, which opens to show it. One change at
   a time for the whole page: the bag can be drawn again in the middle of one. */
const DISCOUNT_NOTE = 'luxe-discount-note';
let discountNote = null;
let discountBusy = false;
const sameCode = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const onCartPage = () => window.themeVariables?.settings?.pageType === 'cart';
const readCart = () => fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => {
  if (!r.ok) throw new Error(`cart ${r.status}`);
  return r.json();
});
const cartCodes = async () => {
  const cart = await readCart();
  const entries = cart.discount_codes || [];
  return { held: entries.map((entry) => entry.code), applied: entries.filter((entry) => entry.applicable).map((entry) => entry.code) };
};

/* a Club code fitted to the bag (owner, 28/10/2026: «applicalo e semplicemente aggiusta poi … se aggiungo al carrello
   poi un altro prodotto non scontato, allora lo sconto piu alto sarà applicato in automatico e se rimane qualcosa fuori
   sarà aggiornata la scritta. idem se cancello un prodotto dal carrello»). A Club code the bag cannot take whole (its
   10% of the full-price products) is divided by the club (/apps/wishlist/loyalty/bag, fitLoyaltyCoupon): the part the
   bag takes goes in, the rest waits in a second code, and a line under the field says so. Each later change of the bag
   fits it again: more of it as the bag grows, less as it shrinks, the whole code back in one when the bag takes it. */
const CLUB_FIT = 'luxe-club-fit';
const isClubCode = (code) => /^SISES-/i.test(String(code || '').trim());
// the Club's codes are in euros: written in the shop's own format, as the bag's totals (€16,00)
const clubMoney = (cents) => {
  const format = window.themeVariables?.settings?.moneyFormat || '€{{amount_with_comma_separator}}';
  const [whole, part] = (cents / 100).toFixed(2).split('.');
  const group = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  const amounts = { amount: `${group(',')}.${part}`, amount_with_comma_separator: `${group('.')},${part}`, amount_no_decimals: group(','), amount_no_decimals_with_comma_separator: group('.') };
  const kind = /\{\{\s*(\w+)\s*\}\}/.exec(format)?.[1];
  return amounts[kind] ? format.replace(/\{\{\s*\w+\s*\}\}/, amounts[kind]) : `€${amounts.amount_with_comma_separator}`;
};
const savedClubFit = () => { try { return JSON.parse(sessionStorage.getItem(CLUB_FIT) || 'null'); } catch { return null; } };
const saveClubFit = (fit) => {
  const kept = fit && fit.later ? { code: fit.now.code, nowCents: fit.now.valueCents, laterCents: fit.later.valueCents, laterCode: fit.later.code, totalCents: fit.totalCents } : null;
  try { if (kept) sessionStorage.setItem(CLUB_FIT, JSON.stringify(kept)); else sessionStorage.removeItem(CLUB_FIT); } catch { /* this page only */ }
};
async function fitClubCode(code, cart) {
  const lines = (cart.items || []).map((item) => ({ productId: item.product_id, variantId: item.variant_id, quantity: item.quantity,
    priceCents: item.final_price, compareAtPriceCents: item.original_price > item.final_price ? item.original_price : null, productTags: [] }));
  const response = await fetch('/apps/wishlist/loyalty/bag', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ code, lines })
  });
  const answer = await response.json().catch(() => null);
  return response.ok && answer?.success ? answer : null;
}
let clubFitting = false;
let clubFitTimer = 0;
async function refitClubCode() {
  if (clubFitting || discountBusy) return;
  clubFitting = true;
  try {
    const cart = await readCart();
    const held = (cart.discount_codes || []).map((entry) => entry.code);
    const code = held.find(isClubCode);
    if (!code) { saveClubFit(null); return; }
    const fit = await fitClubCode(code, cart).catch(() => null);
    if (!fit || fit.action === 'none') return;
    saveClubFit(fit);
    if (sameCode(fit.now.code, code)) {
      document.querySelectorAll('luxe-cart-discount').forEach((box) => box.showClubFit?.());
      return;
    }
    discountBusy = true;
    try {
      redrawBag(await sendCodes(held.map((known) => (sameCode(known, code) ? fit.now.code : known))), null);
    } finally {
      discountBusy = false;
    }
  } catch { /* the bag keeps the code it holds */ } finally {
    clubFitting = false;
  }
}
// any change of the bag but this code's own (a size, a quantity, a product added or taken out)
document.addEventListener('cart:change', (event) => {
  if (event.detail?.baseEvent === 'discount:update') return;
  clearTimeout(clubFitTimer);
  clubFitTimer = setTimeout(refitClubCode, 300);
});
async function sendCodes(codes) {
  const bundled = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: bundled } }));
  const response = await fetch(`${shopRoot()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(','), sections: bundled.join(',') })
  });
  if (!response.ok) throw new Error(`cart/update ${response.status}`);
  return { cart: await response.json(), bundled };
}
function redrawBag({ cart, bundled }, note) {
  discountNote = note;
  if (onCartPage()) {
    try { sessionStorage.setItem(DISCOUNT_NOTE, JSON.stringify(note)); } catch { /* the page comes back without it */ }
    location.reload();
    return;
  }
  if (bundled.length && bundled.every((id) => cart.sections?.[id])) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'discount:update', cart } }));
  } else {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
}
class LuxeCartDiscount extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this);
    this.addEventListener('keydown', this);
    this.showClubFit();
    // the note beside the code's question keeps its one line: the question leaves it exactly its width, measured once
    // the bag shows it (a closed drawer has none)
    const row = this.closest('.luxe-cart-extras');
    const noteButton = row?.querySelector('.luxe-cart-extras__note');
    if (noteButton && 'ResizeObserver' in window) {
      new ResizeObserver(([entry]) => {
        const width = entry.target.offsetWidth;
        if (width) row.style.setProperty('--luxe-note-room', `${width + 16}px`);
      }).observe(noteButton);
    }
    let note = discountNote;
    if (!note && onCartPage()) {
      try { note = JSON.parse(sessionStorage.getItem(DISCOUNT_NOTE) || 'null'); } catch { note = null; }
    }
    if (!note || note.scope !== this.dataset.scope) return;
    discountNote = null;
    try { sessionStorage.removeItem(DISCOUNT_NOTE); } catch { /* nothing was kept */ }
    this.querySelector('details')?.setAttribute('open', '');
    if (note.code && !this.lists(note.code)) this.say(this.dataset.atCheckout, note.code);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this);
    this.removeEventListener('keydown', this);
  }
  handleEvent(event) {
    if (event.type === 'keydown') {
      if (event.key === 'Enter' && !event.isComposing && event.target.matches?.('[data-luxe-discount-input]')) {
        event.preventDefault();
        this.apply();
      }
      return;
    }
    if (event.target.closest?.('[data-luxe-discount-apply]')) {
      event.preventDefault();
      this.apply();
      return;
    }
    const cross = event.target.closest?.('[data-luxe-discount-remove]');
    if (cross) {
      event.preventDefault();
      this.drop(cross.closest('[data-luxe-discount-code]')?.dataset.luxeDiscountCode);
    }
  }
  lists(code) {
    return [...this.querySelectorAll('[data-luxe-discount-code]')].some((item) => sameCode(item.dataset.luxeDiscountCode, code));
  }
  // what a Club code fitted to the bag takes now, and the code where the rest waits
  showClubFit() {
    const line = this.querySelector('[data-luxe-club-note]');
    if (!line) return;
    const fit = savedClubFit();
    const shown = Boolean(fit && this.lists(fit.code));
    line.hidden = !shown;
    line.textContent = shown
      ? (this.dataset.clubPart || '').replace('%NOW%', () => clubMoney(fit.nowCents)).replace('%TOTAL%', () => clubMoney(fit.totalCents))
        .replace('%LATER%', () => clubMoney(fit.laterCents)).replace('%CODE%', () => fit.laterCode)
      : '';
  }
  // the region is shown empty first and filled a moment later, so a screen reader announces it
  say(text, code = '', error = false) {
    const box = this.querySelector('[data-luxe-discount-message]');
    if (!box) return;
    const message = (text || '').replace('%CODE%', () => code);
    box.textContent = '';
    box.hidden = !message;
    box.classList.toggle('is-error', error);
    if (!message) return;
    this.querySelector('details')?.setAttribute('open', '');
    setTimeout(() => { box.textContent = message; }, 60);
  }
  async busyWith(work) {
    if (discountBusy) return;
    discountBusy = true;
    this.setAttribute('aria-busy', 'true');
    this.say('');
    try { await work(); } catch { this.say(this.dataset.failed, '', true); } finally {
      discountBusy = false;
      this.removeAttribute('aria-busy');
    }
  }
  apply() {
    const input = this.querySelector('[data-luxe-discount-input]');
    const code = input?.value.trim();
    if (!code) { input?.focus(); return; }
    return this.busyWith(async () => {
      const { held, applied } = await cartCodes();
      if (applied.some((known) => sameCode(known, code))) {
        input.value = '';
        if (!this.lists(code)) this.say(this.dataset.atCheckout, code);
        return;
      }
      const before = held.filter((known) => !sameCode(known, code));
      const sent = await sendCodes([...before, code]);
      const entry = (sent.cart.discount_codes || []).find((item) => sameCode(item.code, code));
      if (!entry?.applicable) {
        // a Club code larger than the bag allows: the part the bag takes goes in, the rest waits in a second code
        const fit = isClubCode(code) ? await fitClubCode(code, sent.cart).catch(() => null) : null;
        if (fit && fit.action !== 'none') {
          const fitted = await sendCodes([...before, fit.now.code]);
          if ((fitted.cart.discount_codes || []).some((item) => sameCode(item.code, fit.now.code) && item.applicable)) {
            input.value = '';
            saveClubFit(fit);
            redrawBag(fitted, { scope: this.dataset.scope, code: fit.now.code });
            return;
          }
        }
        // the cart would keep a code it does not apply: the cart goes back to the codes it held
        await sendCodes(held);
        this.say(fit?.action === 'none' ? this.dataset.clubNone : this.dataset.invalid, code, true);
        input.select();
        return;
      }
      if (isClubCode(code)) saveClubFit(null);
      redrawBag(sent, { scope: this.dataset.scope, code });
    });
  }
  drop(code) {
    if (!code) return;
    return this.busyWith(async () => {
      const { held } = await cartCodes();
      redrawBag(await sendCodes(held.filter((known) => !sameCode(known, code))), { scope: this.dataset.scope });
    });
  }
}
if (!customElements.get('luxe-cart-discount')) customElements.define('luxe-cart-discount', LuxeCartDiscount);

/* ---------- the look page's sizes (snippets/outfit-product-card*.liquid): a size button picks its size in the card's
   menu and fires the menu's change, so assets/outfit-page.js does the rest (price, "Aggiungi" or "Avvisami", the
   look's total); the buttons follow the menu, which the page's script also sets (first available size, the "add the
   whole look" confirmation). The menu stays in the page for the script, out of sight and out of the tab order. */
const syncSizes = (card) => {
  const select = card?.querySelector('.op-variant');
  if (!select) return;
  card.querySelectorAll('.op-size[data-variant]').forEach((button) => {
    const on = button.dataset.variant === select.value;
    button.classList.toggle('is-selected', on);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
};
document.addEventListener('click', (event) => {
  const button = event.target.closest('.op-size[data-variant]');
  const card = button?.closest('.op-card');
  const select = card?.querySelector('.op-variant');
  if (!select) return;
  // a sold-out size opens the back-in-stock popup at once, the size chosen stays (owner, 04/10, as in the prototype)
  if (button.classList.contains('is-soldout') && window.SkiSisesBisMini) {
    const option = [...select.options].find((o) => o.value === button.dataset.variant);
    const title = card.querySelector('.op-card-title');
    const image = card.querySelector('.op-card-img');
    try {
      const params = { product_id: card.dataset.product || '', variant_id: button.dataset.variant };
      if (typeof window.gtag === 'function') window.gtag('event', 'back_in_stock_open', params);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'back_in_stock_open', ...params });
    } catch { /* tracking never blocks */ }
    window.SkiSisesBisMini.open({
      productId: card.dataset.product || '',
      productHandle: card.dataset.handle || '',
      productTitle: title ? title.textContent.trim() : '',
      productVendor: card.dataset.vendor || '',
      productImage: image ? image.getAttribute('src') || '' : '',
      variantId: button.dataset.variant,
      variantTitle: option ? option.textContent.trim() : button.textContent.trim(),
      sku: option?.dataset.sku || '',
      price: option?.dataset.price || ''
    });
    return;
  }
  select.value = button.dataset.variant;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  syncSizes(card);
});
document.addEventListener('change', (event) => {
  if (event.target.matches?.('.op-variant')) syncSizes(event.target.closest('.op-card'));
}, true);
const initSizes = () => document.querySelectorAll('.op-card').forEach((card) => {
  if (!card.querySelector('.op-sizes')) return;
  const select = card.querySelector('.op-variant');
  if (select) { select.tabIndex = -1; select.setAttribute('aria-hidden', 'true'); }
  syncSizes(card);
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSizes, { once: true });
else initSizes();

/* ---------- the size advisor over the look (owner, 04/10): "Non sei sicuro della tua taglia?" opens the size advisor for
   that piece without leaving the look, through the advisor's open() (integrated1 sizesuggestions, loaded on the first
   click with the piece's sizes from data-luxe-advisor). The size it recommends is chosen in the piece's own sizes; a
   sold-out recommendation opens the look's back-in-stock popup. A piece without a chart shows the size guide instead;
   where the advisor cannot open (an older copy of its script), the link opens the product page's own. ---------- */
const ADVISOR_SRC = 'https://shopify-sync-app-h36w.onrender.com/size-suggestions/static/size_suggestions/size-suggestions.js?v=20261021-cors-retry';
let advisorLoading = null;
const advisorApi = () => {
  if (window.SkiSisesSizeAdvisor?.ready) return Promise.resolve(window.SkiSisesSizeAdvisor);
  advisorLoading ||= new Promise((resolve) => {
    const settle = () => resolve(window.SkiSisesSizeAdvisor?.ready ? window.SkiSisesSizeAdvisor : null);
    document.addEventListener('skisises:size-advisor:ready', settle, { once: true });
    const script = document.createElement('script');
    script.src = ADVISOR_SRC;
    script.async = true;
    script.onload = () => setTimeout(settle, 60); // a copy without open() never says it is ready
    script.onerror = () => resolve(null);
    document.head.append(script);
    setTimeout(() => resolve(null), 8000);
  }).then((api) => { if (!api) advisorLoading = null; return api; });
  return advisorLoading;
};
const lookCard = (productId) => [...document.querySelectorAll('.op-card[data-product]')].find((card) => String(card.dataset.product).split('/').pop() === String(productId).split('/').pop());
// the quick buy's product (snippets/luxe-quick-buy-extras.liquid) and its own sizes
const quickExtras = (productId) => [...document.querySelectorAll('quick-buy-modal luxe-quick-extras[data-product-id]')].find((el) => el.dataset.productId === String(productId).split('/').pop());
const quickSizeLabel = (extras, variant) => {
  const input = extras?.closest('quick-buy-modal')?.querySelector(`input[data-luxe-variant="${CSS.escape(variant)}"]`);
  return input ? { input, label: document.querySelector(`label[for="${CSS.escape(input.id)}"]`) } : null;
};
// the back-in-stock popup for a product off its own page (assets/bis-mini.js, loaded the first time)
let bisMiniLoading = null;
const bisMini = (src) => {
  if (window.SkiSisesBisMini) return Promise.resolve(window.SkiSisesBisMini);
  bisMiniLoading ||= new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(window.SkiSisesBisMini || null);
    script.onerror = () => resolve(null);
    document.head.append(script);
  }).then((api) => { if (!api) bisMiniLoading = null; return api; });
  return bisMiniLoading;
};
const quickBackInStock = (extras, variant, variantTitle) => {
  const fallback = () => { location.href = `${shopRoot()}products/${extras.dataset.handle}?variant=${variant}`; };
  if (!extras.dataset.bisSrc) { fallback(); return; }
  try {
    const params = { product_id: extras.dataset.productId, variant_id: variant };
    if (typeof window.gtag === 'function') window.gtag('event', 'back_in_stock_open', params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'back_in_stock_open', ...params });
  } catch { /* tracking never blocks */ }
  bisMini(extras.dataset.bisSrc).then((api) => {
    if (!api) { fallback(); return; }
    api.open({ productId: extras.dataset.productId, productHandle: extras.dataset.handle, productTitle: extras.dataset.title, productVendor: extras.dataset.vendor, productImage: extras.dataset.image, variantId: variant, variantTitle: variantTitle || '' });
  });
};
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[data-luxe-advisor]');
  if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  let config;
  try { config = JSON.parse(link.dataset.luxeAdvisor); } catch { return; }
  event.preventDefault();
  link.setAttribute('aria-busy', 'true');
  const card = link.closest('.op-card');
  const modal = link.closest('quick-buy-modal');
  advisorApi()
    .then((api) => (api ? api.open(config, link).then((opened) => (opened ? 'open' : 'no-chart')) : 'page'))
    .catch(() => 'page')
    .then((outcome) => {
      link.removeAttribute('aria-busy');
      if (outcome === 'page') location.href = link.href;
      else if (outcome === 'no-chart') (card?.querySelector('.op-help button.op-link') || modal?.querySelector('#sc-btn'))?.click();
    });
});
// the advisor's size, chosen in the piece's or the quick buy's own sizes
document.addEventListener('skisises:size-advisor:result', (event) => {
  const detail = event.detail || {};
  const variant = String(detail.variantId || '').split('/').pop();
  if (!variant) return;
  const select = lookCard(detail.productId)?.querySelector('select.op-variant');
  if (select) {
    if (select.value === variant || ![...select.options].some((o) => o.value === variant)) return;
    select.value = variant;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }
  const size = quickSizeLabel(quickExtras(detail.productId), variant);
  if (size?.label && !size.input.checked && !size.label.classList.contains('is-disabled')) size.label.click();
});
// a sold-out recommendation: the look's or the quick buy's own back-in-stock popup
document.addEventListener('skisises:size-advisor:back-in-stock', (event) => {
  const detail = event.detail || {};
  const variant = String(detail.variantId || '').split('/').pop();
  const button = lookCard(detail.productId)?.querySelector(`.op-size[data-variant="${CSS.escape(variant)}"]`);
  if (button && window.SkiSisesBisMini) { event.preventDefault(); button.click(); return; }
  const extras = quickExtras(detail.productId);
  if (!extras) return;
  event.preventDefault();
  quickBackInStock(extras, variant, detail.variantTitle || detail.recommendedSize || '');
});
// the quick buy's heart (on its picture's corner): the wishlist app's own, its saved state shown as soon as the quick
// buy is in the page
if (!customElements.get('luxe-quick-wish')) {
  customElements.define('luxe-quick-wish', class extends HTMLElement {
    connectedCallback() {
      const heart = this.querySelector('[data-luxe-wish]');
      const register = () => {
        const wl = window.IntegratedWishlist;
        if (!heart || !wl) return;
        try { wl.registerButton?.(this.dataset.handle, heart); } catch { /* the heart stays empty */ }
        if (wl.isSaved?.(this.dataset.handle)) heart.dataset.selected = 'true';
      };
      if (window.IntegratedWishlist) register();
      else document.addEventListener('integrated:wishlist:ready', register, { once: true });
    }
  });
}
document.addEventListener('click', (event) => {
  const heart = event.target.closest('luxe-quick-wish [data-luxe-wish]');
  if (!heart) return;
  const extras = heart.closest('luxe-quick-wish');
  const wl = window.IntegratedWishlist;
  try {
    const params = { source: 'quick_buy', item_id: extras.dataset.productId };
    if (typeof window.gtag === 'function') window.gtag('event', 'save_product', params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'save_product', ...params });
  } catch { /* tracking never blocks */ }
  if (wl?.open) wl.open({ handle: extras.dataset.handle, productId: extras.dataset.productId, source: 'quick_buy', button: heart });
  else location.href = `${shopRoot()}products/${extras.dataset.handle}`;
});

/* ---------- the quick buy's colours (owner, 17/10): the product page's "product_variations" block, the colourways of
   custom.aaa, are links to their pages; in the quick buy a colour draws its product in the open quick buy instead, the
   way Prestige's quick-buy-modal draws one (its #quick-buy-content template, from the product's page). The page is
   asked for when the pointer or a finger reaches the colour, so the swap is usually immediate; the card's own product
   comes back at the next opening, a modified click keeps the link and a failure opens the colour's page. */
const quickColourPages = new Map();
const quickColourUrl = (link) => {
  try {
    const url = new URL(link.href);
    const handle = url.pathname.split('/products/')[1]?.split('/')[0];
    return handle ? `${shopRoot()}products/${handle}${url.search}` : '';
  } catch { return ''; }
};
const quickColourPage = (url) => {
  if (!quickColourPages.has(url)) {
    if (quickColourPages.size >= 12) quickColourPages.delete(quickColourPages.keys().next().value);
    quickColourPages.set(url, fetch(url).then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status))))).catch((error) => { quickColourPages.delete(url); throw error; }));
  }
  return quickColourPages.get(url);
};
const quickColourLink = (target) => target?.closest?.('quick-buy-modal .variant-picker__option-values a[href*="/products/"]:not(.is-selected)');
const preloadQuickColour = (event) => {
  const link = quickColourLink(event.target);
  const url = link && quickColourUrl(link);
  if (url) quickColourPage(url).catch(() => {});
};
document.addEventListener('pointerenter', preloadQuickColour, true);
document.addEventListener('touchstart', preloadQuickColour, { capture: true, passive: true });
document.addEventListener('focusin', preloadQuickColour);
const quickColourGen = new WeakMap();
document.addEventListener('click', async (event) => {
  const link = event.target.closest('quick-buy-modal .variant-picker__option-values a[href*="/products/"]');
  if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const url = quickColourUrl(link);
  if (!url) return;
  event.preventDefault();
  if (link.classList.contains('is-selected')) return;
  const modal = link.closest('quick-buy-modal');
  // a closing quick buy, or a newer colour, leaves this answer unused (the quick buy empties itself once closed)
  const content = link.closest('.quick-buy-modal__content');
  const gen = (quickColourGen.get(modal) || 0) + 1;
  quickColourGen.set(modal, gen);
  const current = () => quickColourGen.get(modal) === gen && content?.isConnected && modal.hasAttribute('open');
  content?.setAttribute('aria-busy', 'true');
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
  try {
    // a page that does not come in 8 s opens as before
    const html = await withTimeout(quickColourPage(url), 8000).catch((error) => { quickColourPages.delete(url); throw error; });
    // the tap paints first: a preloaded page would otherwise be parsed and drawn in the tap's own task
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    if (!current()) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('quick-buy-content')?.content;
    if (!fresh) throw new Error('no quick buy');
    fresh.querySelectorAll('noscript').forEach((tag) => tag.remove());
    modal.replaceChildren(fresh);
    try { window.Shopify?.PaymentButton?.init(); } catch { /* the add button stays */ }
    modal.querySelector('.variant-picker__option-values a.is-selected')?.focus({ preventScroll: true });
  } catch {
    if (current()) location.href = link.href;
  } finally {
    content?.removeAttribute('aria-busy');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  }
});

/* ---------- the bag's products open their quick buy (owner, 17/10): a product of the bag (the drawer's lines and its
   suggestion cards, the cart page's lines) opens its quick buy over the bag, on the line's own size, instead of its
   page, which "Visualizza dettagli" in the quick buy leads to. One Prestige quick-buy-modal, put in the page at the
   first tap, serves them all (a modal stands over the drawer). A modified click keeps the link, and a failure opens
   the page. The suggestions' sks_rec tag (refreshSuggest above) used to reach the analytics with the page it opened:
   it now goes with a quick_buy_open event. */
let bagQuickBuy = null;
let bagQuickBuyPending = false;
const bagProductLink = (target) => {
  const link = target.closest('a[href*="/products/"]');
  if (!link || link.origin !== location.origin || link.closest('quick-buy-modal')) return null;
  return link.closest('#cart-drawer line-item, #cart-drawer .horizontal-product-card, form.cart-page line-item') ? link : null;
};
document.addEventListener('click', async (event) => {
  if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = bagProductLink(event.target);
  if (!link || !customElements.get('quick-buy-modal')) return;
  let handle = '';
  let source = 'cart';
  try {
    const url = new URL(link.href);
    handle = url.pathname.split('/products/')[1]?.split('/')[0] || '';
    const variant = url.searchParams.get('variant');
    if (handle && variant) handle += `?variant=${encodeURIComponent(variant)}`;
    source = url.searchParams.get('sks_rec') || source;
  } catch { return; }
  if (!handle) return;
  event.preventDefault();
  if (bagQuickBuyPending || bagQuickBuy?.hasAttribute('open')) return;
  bagQuickBuyPending = true;
  try {
    if (!bagQuickBuy?.isConnected) {
      bagQuickBuy = document.createElement('quick-buy-modal');
      bagQuickBuy.id = 'luxe-bag-quick-buy';
      bagQuickBuy.className = 'quick-buy-modal modal';
      document.body.append(bagQuickBuy);
    }
    // still closing: the theme empties it when its leave animation ends, which would empty the new product too
    if (bagQuickBuy.style.display === 'block') {
      await Promise.race([new Promise((resolve) => bagQuickBuy.addEventListener('dialog:after-hide', resolve, { once: true })), new Promise((resolve) => setTimeout(resolve, 800))]);
    }
    try {
      const params = { source, item_handle: handle.split('?')[0] };
      if (typeof window.gtag === 'function') window.gtag('event', 'quick_buy_open', params);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'quick_buy_open', ...params });
    } catch { /* tracking never blocks */ }
    bagQuickBuy.setAttribute('handle', handle);
    // once it is open, its own open attribute guards it
    bagQuickBuy.addEventListener('dialog:before-show', () => { bagQuickBuyPending = false; }, { once: true });
    await bagQuickBuy.show();
  } catch {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    location.href = link.href;
  } finally {
    bagQuickBuyPending = false;
  }
});

/* ---------- the size advisor asked for from a look, on the product page (owner, 04/10): the look's link falls back to
   the product with #sz-open, and the product page opens its advisor as soon as the advisor has found the product's
   chart (its button shows then); without a chart the page simply stays. ---------- */
if (location.hash === '#sz-open') {
  const started = Date.now();
  const openAdvisor = () => {
    const button = document.getElementById('sz-btn');
    if (button && button.offsetWidth > 0) {
      history.replaceState(history.state, '', location.pathname + location.search);
      button.scrollIntoView({ block: 'center' });
      button.click();
      return;
    }
    if (Date.now() - started < 8000) setTimeout(openAdvisor, 250);
  };
  openAdvisor();
}

/* ---------- the grids' cards on a touch screen (snippets/product-card.liquid, slides: true): the other pictures are
   made at the first touch, before the gesture scrolls (the next one at once, the others as they come near), and the
   dots follow the scroll. A computer never touches a card, so it downloads none of them. */
const loadSlides = (track) => {
  const list = track.dataset.slides;
  if (!list) return;
  track.removeAttribute('data-slides');
  let slides = [];
  try { slides = JSON.parse(list); } catch { return; }
  const sizes = track.querySelector('.product-card__image--primary')?.getAttribute('sizes') || '';
  slides.forEach(([src, srcset], index) => {
    const img = document.createElement('img');
    img.className = 'product-card__image product-card__image--slide';
    img.alt = '';
    img.draggable = false;
    img.decoding = 'async';
    if (index) img.loading = 'lazy';
    img.sizes = sizes;
    img.srcset = srcset;
    img.src = src;
    track.append(img);
  });
};
document.addEventListener('touchstart', (event) => {
  const track = event.target.closest?.('.product-card__slides[data-slides]');
  if (track) loadSlides(track);
}, { passive: true, capture: true });
document.addEventListener('scroll', (event) => {
  const track = event.target;
  if (!(track instanceof Element) || !track.classList.contains('product-card__slides')) return;
  loadSlides(track);
  const current = Math.round(track.scrollLeft / track.clientWidth);
  track.closest('.product-card__figure')?.querySelectorAll('.product-card__dots i').forEach((dot, index) => dot.classList.toggle('is-current', index === current));
}, { passive: true, capture: true });

/* ---------- home, Daily Inspiration (sections/daily-inspiration-native.liquid): from 1024px the band stays under the
   header while the page goes down, and the looks travel sideways with the scroll, as in the design; phones keep their
   swipe, reduced motion the plain rail. The track stays the section's own scroller (its persona order, drag and look
   viewer keep working); only its position follows the page. */
const diSection = document.querySelector('.section-daily-inspiration .di-section');
const diTrack = diSection?.querySelector('[data-di-track]');
if (diSection && diTrack) {
  const wide = matchMedia('(min-width: 1024px)');
  let travel = 0;
  let top = 0;
  let frame = 0;
  const run = () => Math.max(1, diSection.offsetHeight - (innerHeight - top));
  const follow = () => {
    frame = 0;
    if (!travel) return;
    const progress = Math.min(1, Math.max(0, (top - diSection.getBoundingClientRect().top) / run()));
    diTrack.scrollLeft = Math.round(progress * travel);
  };
  const pin = () => {
    diSection.classList.remove('luxe-di-pinned');
    travel = 0;
    if (!wide.matches || reduced.matches) return;
    const sticky = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height'));
    top = sticky > 0 ? sticky : (document.querySelector('.shopify-section--header')?.offsetHeight || 0);
    diSection.style.setProperty('--luxe-di-top', `${top}px`);
    diSection.classList.add('luxe-di-pinned');
    const distance = diTrack.scrollWidth - diTrack.clientWidth;
    if (distance <= 40) { diSection.classList.remove('luxe-di-pinned'); return; }
    travel = distance;
    diSection.style.setProperty('--luxe-di-travel', `${travel}px`);
    follow();
  };
  addEventListener('scroll', () => { if (travel && !frame) frame = requestAnimationFrame(follow); }, { passive: true });
  let resizeTimer = 0;
  addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(pin, 200); }, { passive: true });
  wide.addEventListener('change', pin);
  reduced.addEventListener('change', pin);
  // a look reached with the keyboard brings the page to where the track shows it
  diTrack.addEventListener('focusin', (event) => {
    const card = event.target.closest('.di-polaroid');
    if (!travel || !card) return;
    const at = card.getBoundingClientRect().left - diTrack.getBoundingClientRect().left + diTrack.scrollLeft;
    const progress = Math.min(1, Math.max(0, at / travel));
    scrollTo({ top: diSection.getBoundingClientRect().top + scrollY - top + run() * progress, behavior: 'auto' });
  });
  pin();
  if (document.readyState !== 'complete') addEventListener('load', pin, { once: true });
}

/* ---------- the sport band's ridge (snippets/luxe-ridge.liquid): as the band comes up the screen, the green line is
   uncovered from left to right across the screen (the prototype's 1.6 s and mask easing) and each peak's name appears
   as the line reaches it (owner, 03-04/10). The line has its own layer, clipped in the page's pixels: a dash on the
   path itself was measured on the scaled drawing and showed the line already there on most screens. The hidden start
   is set here, so without this script the line and labels are simply there. */
const MASK_EASE = [0.76, 0, 0.24, 1];
const bezier = (a, b) => (t) => 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
// the moment (0-1 of the drawing's time) at which the mask easing has uncovered a given share of the width
const momentFor = (share) => {
  const x = bezier(MASK_EASE[0], MASK_EASE[2]);
  const y = bezier(MASK_EASE[1], MASK_EASE[3]);
  for (let t = 0; t <= 1; t += 0.01) if (y(t) >= share) return x(t);
  return 1;
};
document.querySelectorAll('.ridge').forEach((ridge) => {
  const draw = ridge.querySelector('.ridge__draw');
  const peaks = [...ridge.querySelectorAll('.ridge__peak')];
  if (!draw || reduced.matches) return;
  const DURATION = 1.6;
  draw.style.clipPath = 'inset(0 100% 0 0)';
  peaks.forEach((peak) => { peak.style.opacity = '0'; });
  inView(ridge, () => {
    animate(draw, { clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'] }, { duration: DURATION, easing: MASK_EASE }).finished.then(() => {
      draw.style.clipPath = '';
    });
    const box = ridge.getBoundingClientRect();
    peaks.forEach((peak) => {
      const r = peak.getBoundingClientRect();
      const x = peak.classList.contains('ridge__peak--left') ? r.right : r.left;
      const share = Math.min(1, Math.max(0, (x - box.left) / (box.width || 1)));
      animate(peak, { opacity: [0, 1] }, { duration: 0.48, delay: momentFor(share) * DURATION, easing: [0.16, 1, 0.3, 1] });
    });
  }, { margin: '0px 0px -8% 0px' });
});

/* ---------- the bag (the owner's picks on the prototype, 03/10): the total in the checkout button rolls to its new
   value, digit by digit, once the drawer is open; the first time the order reaches free shipping, a hand-drawn green
   line underlines the message. Prestige replaces the drawer's content in its own "cart:change" listener, which was
   added before this module ran, so here the new content is already in place. ---------- */
const REEL = '01234567890123456789'.replace(/\d/g, '<span>$&</span>');
const esc = (text) => String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const amount = (text) => parseFloat(String(text).replace(/[^\d,]/g, '').replace(',', '.')) || 0;

function roll(el, from, to, drawer) {
  const up = amount(to) >= amount(from);
  const a = [...String(from)];
  const b = [...String(to)];
  const offset = a.length - b.length;
  const ends = [];
  const html = b.map((ch, i) => {
    if (!/\d/.test(ch)) return `<span>${esc(ch)}</span>`;
    const f = /\d/.test(a[i + offset] || '') ? +a[i + offset] : 0;
    const t = +ch;
    ends.push(up ? (t >= f ? t : t + 10) : t);
    return `<span class="luxe-roll__r"><span class="luxe-roll__ph">${ch}</span><span class="luxe-roll__s" style="--p:${up ? f : (t > f ? f + 10 : f)};--i:${ends.length - 1}">${REEL}</span></span>`;
  }).join('');
  el.innerHTML = `<span class="sr-only">${esc(to)}</span><span class="luxe-roll" aria-hidden="true">${html}</span>`;
  const go = () => {
    const strips = el.querySelectorAll('.luxe-roll__s');
    void el.offsetWidth; // the reels are painted at their start before they turn
    strips.forEach((strip, i) => { strip.classList.add('is-go'); strip.style.setProperty('--p', ends[i]); });
    setTimeout(() => { if (el.isConnected) el.textContent = to; }, 880 + 50 * strips.length);
  };
  if (drawer && !drawer.hasAttribute('open')) drawer.addEventListener('dialog:after-show', go, { once: true });
  else requestAnimationFrame(go);
}

// the subtotal in the bag's summary (sections/cart-drawer.liquid, owner 04/10: the button reads only "Checkout")
const bagTotal = () => document.querySelector('#cart-drawer [data-luxe-subtotal]');
// the threshold from the theme's setting (layout/theme.liquid puts it on this script's tag), so it is known while the
// bag is empty and has no bar; the cart's total for shipping as Prestige's own bar counts it
const FREE_AT = parseFloat(String(document.querySelector('script[data-luxe-free-threshold]')?.dataset.luxeFreeThreshold || '').replace(/[^0-9.]/g, '')) * 100 || null;
// the bag's destination (snippets/luxe-cart-shipping.liquid), else the page's country: free shipping exists in Italy only
const shipCountry = () => document.querySelector('#cart-drawer [data-luxe-ship]')?.dataset.country || window.Shopify?.country;
const freeShipping = (cart) => {
  if (shipCountry() && shipCountry() !== 'IT') return false;
  if (cart && FREE_AT) {
    const shipped = cart.items.filter((item) => item.requires_shipping).reduce((sum, item) => sum + item.final_line_price, 0);
    const discounts = (cart.cart_level_discount_applications || []).reduce((sum, d) => sum + d.total_allocated_amount, 0);
    return shipped - discounts >= FREE_AT;
  }
  const bar = document.querySelector('#cart-drawer free-shipping-bar');
  return bar ? bar.totalPrice >= parseFloat(bar.getAttribute('threshold')) : false; // no bar: an empty bag
};
const shipBar = () => document.querySelector('#cart-drawer free-shipping-bar');
const progressOf = (bar) => parseFloat(bar?.style.getPropertyValue('--luxe-progress')) || 0;
// the delivery window's dates with the weekday in the visitor's language ("lun 5 ott"), the server's numeric date
// staying as the fallback
const dayFormat = (() => {
  try { return new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (error) { return null; }
})();
const localDays = (root = document) => {
  if (!dayFormat) return;
  root.querySelectorAll('time[data-luxe-day]:not([data-luxe-done])').forEach((time) => {
    const [y, m, d] = (time.getAttribute('datetime') || '').split('-').map(Number);
    if (!y || !m || !d) return;
    time.textContent = dayFormat.format(new Date(Date.UTC(y, m - 1, d))).replace(/,/g, '');
    time.dataset.luxeDone = '';
  });
};
localDays();
// Prestige swaps the drawer's children after an awaited cart read, so the new content is looked for frame by frame
// (its header element is new each time) and finished before it is painted
const afterRedraw = (drawer, marker, done, frames = 40) => {
  const header = drawer?.querySelector('[slot="header"]');
  if (header && header !== marker) { done(); return; }
  if (frames > 0) requestAnimationFrame(() => afterRedraw(drawer, marker, done, frames - 1));
};
// the shipping line eases from where it was (from nothing on the first add) to the new amount
const easeBar = (from) => {
  const line = shipBar()?.querySelector('.luxe-ship-bar');
  const to = progressOf(shipBar());
  if (!line || to === from || reduced.matches || !line.animate) return;
  try {
    line.animate({ transform: [`scaleX(${from})`, `scaleX(${to})`] }, { duration: 640, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', pseudoElement: '::before' });
  } catch (error) { /* an engine without pseudo-element animations shows the new amount */ }
};
let lastTotal = bagTotal()?.textContent.trim() || null;
let lastFree = freeShipping();
document.addEventListener('cart:change', (event) => {
  // the drawer draws its new content just after the event: wait for the new total, then roll from the old one
  const before = lastTotal;
  const drawer = document.getElementById('cart-drawer');
  const oldBar = shipBar();
  const from = oldBar ? progressOf(oldBar) : 0;
  afterRedraw(drawer, drawer?.querySelector('[slot="header"]'), () => { localDays(drawer); easeBar(from); });
  let tries = 12;
  const settle = () => {
    const total = bagTotal();
    const now = total?.textContent.trim() || null;
    if (total && before && now && now !== before && !total.querySelector('.luxe-roll')) {
      lastTotal = now;
      if (!reduced.matches) roll(total, before, now, drawer);
      return;
    }
    if (--tries > 0) setTimeout(settle, 120);
    else lastTotal = now;
  };
  settle();
  // reaching free shipping: the message's hand-drawn line is drawn once (assets/luxe.css, .luxe-ship-draw)
  const free = freeShipping(event.detail?.cart);
  if (free && lastFree === false && drawer && !reduced.matches) {
    drawer.classList.add('luxe-ship-draw');
    setTimeout(() => drawer.classList.remove('luxe-ship-draw'), 2400);
  }
  lastFree = free;
});

/* ---------- the bag's shipping line (snippets/luxe-cart-shipping.liquid, owner 20/10): the destination's real rate for
   this cart, asked of Shopify (/cart/prepare_shipping_rates.json, then async_shipping_rates.json until it answers) when
   the bag opens and each time it is drawn again while open, and written on the line and in the summary: the cheapest
   rate, "Gratuita" at 0, "Non spediamo in …" without one, "calcolata al pagamento" when Shopify does not answer. The
   answers wait in the session for the same country and cart. Choosing another country sends the line's localization
   form without leaving the page: the shop's country changes (the checkout opens with it) and the bag is drawn again in
   it, or the page loads again when the currency changes with it. ---------- */
// Shopify gives a rate only for an address with a postal code, and a province in IT, ES, PT, IE and RO; the shop's
// rates go by country (Spain: the mainland's), so the capital's address stands for the country (each of the 27 tried
// on the shop on 20/10/2026, each at its zone's rate). A country missing here reads "calcolata al pagamento".
const SHIP_SAMPLE = {
  AT: ['1010'], BE: ['1000'], BG: ['1000'], CY: ['1010'], CZ: ['11000'], DE: ['10115'], DK: ['1050'], EE: ['10111'],
  ES: ['28001', 'M'], FI: ['00100'], FR: ['75001'], GR: ['10557'], HR: ['10000'], HU: ['1011'], IE: ['D02X285', 'D'],
  IT: ['00184', 'RM'], LT: ['01100'], LU: ['1111'], LV: ['LV-1050'], MT: ['VLT1117'], NL: ['1012AB'], PL: ['00-001'],
  PT: ['1100-148', 'PT-11'], RO: ['010011', 'B'], SE: ['11120'], SI: ['1000'], SK: ['81101'],
};
const SHIP_RATES = 'luxe:ship-rates';
const shipBox = () => document.querySelector('#cart-drawer [data-luxe-ship]');
const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
// in the shop's own format for its currency (€7,00, as "| money" writes the subtotal), in the browser's for another
const shipMoney = (cents, currency) => {
  const format = window.themeVariables?.settings?.moneyFormat;
  const shopCurrency = currency === window.Shopify?.currency?.active && Number(window.Shopify?.currency?.rate || 1) === 1;
  const digits = (thousands, decimal, places = 2) => {
    const [whole, part] = (cents / 100).toFixed(places).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (part ? decimal + part : '');
  };
  const kinds = {
    amount: () => digits(',', '.'),
    amount_no_decimals: () => digits(',', '.', 0),
    amount_with_comma_separator: () => digits('.', ','),
    amount_no_decimals_with_comma_separator: () => digits('.', ',', 0),
    amount_with_apostrophe_separator: () => digits("'", '.'),
  };
  const kind = /\{\{\s*(\w+)\s*\}\}/.exec(format || '')?.[1];
  if (shopCurrency && kinds[kind]) return format.replace(/\{\{\s*\w+\s*\}\}/, kinds[kind]());
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};
const savedRates = () => { try { return JSON.parse(sessionStorage.getItem(SHIP_RATES) || '{}') || {}; } catch (error) { return {}; } };
const saveRate = (key, rate) => {
  try {
    const all = { ...savedRates(), [key]: rate };
    const keys = Object.keys(all);
    keys.slice(0, Math.max(0, keys.length - 20)).forEach((old) => { delete all[old]; });
    sessionStorage.setItem(SHIP_RATES, JSON.stringify(all));
  } catch (error) { /* asked again next time */ }
};
// a request Shopify leaves hanging must not hold the questions after it
const timed = () => (typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? { signal: AbortSignal.timeout(8000) } : {});
// the cart as Shopify holds it now, in the line's terms (currency, total, items: snippets/luxe-cart-shipping.liquid)
const cartNow = async () => {
  const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' }, ...timed() });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  const cart = await response.json();
  return `${cart.currency}:${cart.total_price}:${cart.item_count}`;
};
// one question at a time: Shopify keeps one calculation per cart, and its answer is the last destination prepared
let shipQueue = Promise.resolve();
const askRate = (country, key) => {
  const run = async () => {
    const sample = SHIP_SAMPLE[country];
    if (!sample) return null;
    // Prestige opens the bag before drawing it again after an add, so a line can be older than the cart: a rate is
    // asked, and kept, only for the cart the line shows (else a cart's free shipping read as another's)
    const cart = key.slice(key.indexOf(':') + 1);
    if (await cartNow() !== cart) return { stale: true };
    const params = new URLSearchParams({ 'shipping_address[country]': country, 'shipping_address[zip]': sample[0] });
    if (sample[1]) params.set('shipping_address[province]', sample[1]);
    const prepared = await fetch(`${shopRoot()}cart/prepare_shipping_rates.json?${params}`, { method: 'POST', headers: { Accept: 'application/json' }, ...timed() });
    // a refused address (422) is never read on: the answer would be the previous destination's
    if (!prepared.ok) throw new Error(`prepare_shipping_rates ${prepared.status}`);
    for (let attempt = 0; attempt < 10; attempt += 1) {
      await pause(300 + attempt * 200);
      const response = await fetch(`${shopRoot()}cart/async_shipping_rates.json?${params}`, { headers: { Accept: 'application/json' }, ...timed() });
      if (!response.ok) throw new Error(`async_shipping_rates ${response.status}`);
      const answer = await response.json();
      if (!answer) continue; // still calculating
      const rates = (answer.shipping_rates || []).map((rate) => Math.round(parseFloat(rate.price) * 100)).filter(Number.isFinite);
      if (await cartNow() !== cart) return { stale: true };
      return rates.length ? { cents: Math.min(...rates) } : { none: true };
    }
    throw new Error('async_shipping_rates: no answer');
  };
  const asked = shipQueue.then(run, run);
  shipQueue = asked.catch(() => {});
  return asked;
};
const showRate = (box, rate) => {
  const line = box.querySelector('[data-luxe-ship-rate]');
  box.removeAttribute('aria-busy');
  // no answer: what the page already knew stays ("Gratuita" in Italy above the threshold)
  if (!rate && line?.textContent.trim()) return;
  let text = box.dataset.checkout;
  if (rate?.none) text = box.dataset.none;
  else if (rate && rate.cents === 0) text = box.dataset.free;
  else if (rate) text = shipMoney(rate.cents, box.dataset.currency);
  if (line) {
    line.textContent = text;
    line.classList.toggle('is-free', rate?.cents === 0);
    line.classList.toggle('is-none', Boolean(rate?.none));
  }
  const summary = document.querySelector('#cart-drawer [data-luxe-ship-summary]');
  if (summary) summary.textContent = text;
};
// one question per cart and country, shared by the lines drawn meanwhile (look pages draw the bag twice per add)
const asking = new Map();
const redrawnFor = new Set();
const refreshShipping = async (opening = false) => {
  const drawer = document.getElementById('cart-drawer');
  const box = shipBox();
  if (!box || !(opening || drawer?.hasAttribute('open'))) return;
  const key = box.dataset.key;
  if (!key || box.dataset.shown === key || box.dataset.asking === key) return;
  const saved = savedRates()[key];
  if (saved) {
    box.dataset.shown = key;
    showRate(box, saved);
    return;
  }
  box.dataset.asking = key;
  box.setAttribute('aria-busy', 'true');
  if (!asking.has(key)) asking.set(key, askRate(box.dataset.country, key).finally(() => asking.delete(key)));
  let rate = null;
  try {
    rate = await asking.get(key);
  } catch (error) { rate = null; }
  if (rate && !rate.stale) saveRate(key, rate);
  // drawn again meanwhile, or another country chosen: the new line asks for itself
  if (!box.isConnected || box.dataset.key !== key) return;
  delete box.dataset.asking;
  if (rate?.stale) {
    // the cart changed and no new line came: the bag is drawn again once for it
    if (!redrawnFor.has(key)) {
      redrawnFor.add(key);
      box.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }
    rate = null;
  }
  box.dataset.shown = key;
  showRate(box, rate);
};
{
  // Prestige draws the bag again after each change (cart:change, cart:refresh, pageshow): a new line asks again, with
  // its dates in the visitor's language. The drawer watched is the one that opens, as the look pages put a new drawer
  // in place of the old one (assets/outfit-page.js), so the opening is heard on the document.
  let watched = null;
  let pending = false;
  const redrawn = new MutationObserver((records) => {
    if (pending || !records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1))) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      if (watched) localDays(watched);
      refreshShipping();
    });
  });
  const watch = (drawer) => {
    if (!drawer || drawer === watched) return;
    redrawn.disconnect();
    watched = drawer;
    redrawn.observe(drawer, { childList: true, subtree: true });
  };
  watch(document.getElementById('cart-drawer'));
  document.addEventListener('dialog:before-show', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    watch(event.target);
    refreshShipping(true);
  }, true);
}
document.addEventListener('change', async (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-luxe-ship-country]') || !select.form) return;
  const { form } = select;
  const box = select.closest('[data-luxe-ship]');
  markLocalizationChosen();
  // the new code at once; its flag, rate and dates come with the bag drawn again
  if (box) {
    box.dataset.key = ''; // an answer for the country left is not written
    box.setAttribute('aria-busy', 'true');
    const code = box.querySelector('.luxe-ship__code');
    if (code) code.textContent = select.value;
    box.querySelector('.luxe-ship__flag')?.style.setProperty('visibility', 'hidden');
    const line = box.querySelector('[data-luxe-ship-rate]');
    if (line) line.textContent = '';
  }
  try {
    const body = new FormData(form);
    body.set('return_to', `${shopRoot()}cart.js`);
    const response = await fetch(form.getAttribute('action'), { method: 'POST', body, ...timed() });
    if (!response.ok) throw new Error(`localization ${response.status}`);
    // Shopify follows return_to (/cart.js), but answers "{}" to a request that asks for JSON (live, 20/10): the cart
    // is read again when the answer is not it
    let cart = await response.json().catch(() => null);
    if (!cart?.currency) cart = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' }, ...timed() }).then((r) => (r.ok ? r.json() : null));
    if (!cart?.currency) throw new Error('localization: no cart');
    if (box && cart.currency !== box.dataset.currency) {
      location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  } catch (error) {
    // Shopify loads this page again in the new country (the form drawn with the bag returns to the section's page)
    let back = form.querySelector('input[name="return_to"]');
    if (!back) {
      back = Object.assign(document.createElement('input'), { type: 'hidden', name: 'return_to' });
      form.append(back);
    }
    back.value = location.pathname + location.search;
    form.submit();
  }
});

/* ---------- reviewers' names (owner, 04/10: "metti sempre il nome del cliente e il cognome solo la prima lettera con
   il punto"): the first name and the surname's initial, "Jacopo Bruno" as "Jacopo B.", in the reviews rail below and
   in Judge.me's review lists; a name that is an email address is never shown. ---------- */
const shortName = (raw) => {
  const words = String(raw || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  if (!words.length || words.some((word) => word.includes('@'))) return '';
  const first = words[0] === words[0].toLocaleUpperCase() && words[0].length > 2
    ? words[0].charAt(0) + words[0].slice(1).toLocaleLowerCase()
    : words[0].charAt(0).toLocaleUpperCase() + words[0].slice(1);
  if (words.length === 1) return first;
  const last = words[words.length - 1].replace(/[^\p{L}]/gu, '');
  return last ? `${first} ${last.charAt(0).toLocaleUpperCase()}.` : first;
};
(() => {
  const fix = (root) => root.querySelectorAll('.jdgm-rev__author, .jm-reviewer-info__name').forEach((author) => {
    const name = shortName(author.textContent);
    if (author.textContent.trim() !== name) author.textContent = name;
    if (!author.hasAttribute('data-luxe-short')) author.setAttribute('data-luxe-short', '');
  });
  // Judge.me fills its lists after the page and again on every page of reviews: each list is read again then, and a
  // name already short is left alone, so the rewrite never feeds itself (the mark is an attribute, not watched)
  const LISTS = '#judgeme_product_reviews, .jdgm-rev-widg, .jdgm-all-reviews-widget, .jm-review-widget';
  [...document.querySelectorAll(LISTS)].filter((list) => !list.parentElement?.closest(LISTS)).forEach((list) => {
    fix(list);
    new MutationObserver(() => fix(list)).observe(list, { childList: true, subtree: true, characterData: true });
  });
})();

/* ---------- the pre-footer reviews (owner, 04/10: "Lasciamo parlare i clienti per noi" as in the design): Judge.me's
   carousel is read as the source, drawn again as the design's rail and then taken out of the page, the title and the count in
   Judge.me's own words, the rating as "4,8 su 5", the stars, the review in Bodoni between quotes, the reviewer as
   "Jacopo B." and "Cliente verificato, su" the product (its link when Judge.me gives one), never the date; a line
   that fills as the rail scrolls, and the arrows on wide screens. ---------- */
(() => {
  // On a page in another language Judge.me's carousel waits in a template (sections/apps.liquid, owner 03/11/2026): its
  // reviews, almost all in Italian, are drawn only on «Leggi le recensioni», so they are not part of the page Google
  // reads; the stars and the count show at once.
  const held = document.querySelector('template[data-luxe-reviews-source]');
  const source = document.querySelector('.jdgm-carousel-wrapper') || held?.content.querySelector('.jdgm-carousel-wrapper');
  const deferred = !!held && held.content.contains(source);
  let words = null;
  try { words = JSON.parse(document.querySelector('script[data-luxe-reviews-i18n]')?.textContent || 'null'); } catch { words = null; }
  if (!source || !words) return;
  // The rail replaces Judge.me's source before its link observer can see it. Preserve the page's server-owned
  // locale while copying product links, regardless of which deferred script runs first.
  const reviewProductHref = (href) => {
    if (!href || !words.localeRoot || words.localeRoot === '/') return href;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return href;
      const root = '/' + String(words.localeRoot).replace(/^\/+|\/+$/g, '');
      return root + url.pathname + url.search + url.hash;
    } catch {
      return href;
    }
  };
  const seen = new Set();
  const reviews = [];
  source.querySelectorAll('.jdgm-carousel-item').forEach((item) => {
    const id = item.dataset.reviewId;
    if (!id || seen.has(id)) return; // Judge.me repeats items for its loop
    seen.add(id);
    const body = item.querySelector('.jdgm-carousel-item__review-body')?.textContent.replace(/\s+/g, ' ').trim();
    if (!body) return;
    const link = item.querySelector('a.jdgm-carousel-item__product');
    reviews.push({
      id,
      body,
      name: shortName(item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent),
      stars: item.querySelectorAll('.jdgm-carousel-item__review-rating .jdgm--on').length || 5,
      product: item.querySelector('.jdgm-carousel-item__product-title')?.textContent.replace(/\s+/g, ' ').trim() || '',
      href: reviewProductHref(link?.getAttribute('href') || '')
    });
  });
  if (!reviews.length) return;
  const lang = document.documentElement.lang || undefined;
  const score = parseFloat(source.querySelector('[data-score]')?.dataset.score);
  const scoreText = Number.isFinite(score) ? words.score.replace('@@SCORE@@', new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(score)) : '';
  const count = source.querySelector('.jdgm-carousel-number-of-reviews');
  // the title and the count in the visitor's language come from Judge.me's settings, which the page carries
  // already translated (the carousel itself arrives in English and was translated by Judge.me's script later)
  const jm = window.jdgmSettings || {};
  const title = (jm.featured_carousel_title || source.querySelector('.jdgm-carousel-title')?.textContent || '').trim();
  const reviewsNumber = (count?.textContent.match(/\d[\d.,]*/) || [])[0];
  const countText = reviewsNumber && jm.featured_carousel_count_text ? jm.featured_carousel_count_text.replace(/\{\{\s*n\s*\}\}/, reviewsNumber).trim() : (count?.textContent.trim() || '');
  const STAR = '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 2.5l2.94 6.06 6.56.92-4.78 4.62 1.16 6.6L12 17.6l-5.88 3.1 1.16-6.6L2.5 9.48l6.56-.92z"/></svg>';
  const stars = (n) => `<span class="luxe-stars" aria-hidden="true">${STAR.repeat(Math.max(1, Math.min(5, n)))}</span>`;
  const ARROW = (d) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="${d}"/></svg>`;
  const caption = (r) => {
    if (!r.product || r.product === words.shop) return esc(words.verifiedStore);
    const reviewed = !r.href && Object.prototype.hasOwnProperty.call(words.historicalProducts || {}, r.product)
      && typeof words.historicalProducts[r.product] === 'string' ? words.historicalProducts[r.product] : '';
    const name = r.href ? `<a href="${esc(r.href)}">${esc(r.product)}</a>`
      : `<span class="luxe-review__product-title" data-product-title-localized="${!!reviewed}">${esc(reviewed || r.product)}</span>`;
    return esc(words.verifiedOn).replace('@@PRODUCT@@', name);
  };
  const id = 'luxe-reviews-track';
  const cardsHtml = reviews.map((r, index) => `<li class="luxe-review" data-review-id="${esc(r.id)}" data-card-index="${index}"><figure>${stars(r.stars)}<blockquote class="luxe-review__quote">“<span class="jdgm-text">${esc(r.body)}</span>”</blockquote><figcaption>${r.name ? `<span class="luxe-review__name">${esc(r.name)}</span>` : ''}${caption(r)}</figcaption></figure></li>`).join('');
  const rail = document.createElement('div');
  rail.className = 'luxe-reviews';
  rail.innerHTML = `
    <div class="luxe-reviews__head">
      <h2 class="luxe-reviews__title">${esc(title)}</h2>
      <p class="luxe-reviews__score">${stars(5)}${scoreText ? ` <strong>${esc(scoreText)}</strong>` : ''}${countText ? ` <span class="luxe-reviews__count">${count?.querySelector('a') ? `<a href="${esc(count.querySelector('a').getAttribute('href'))}" target="_blank" rel="noopener">${esc(countText)}</a>` : esc(countText)}</span>` : ''}</p>
    </div>
    <ul class="luxe-reviews__track" id="${id}" role="list" tabindex="0" aria-label="${esc(title)}"${deferred ? ' hidden' : ''}>
      ${deferred ? '' : cardsHtml}
    </ul>
    ${deferred ? `<p class="luxe-reviews__open"><button type="button" class="button button--outline" aria-controls="${id}">${esc(words.readReviews)}</button></p>` : ''}
    <div class="luxe-reviews__controls"${deferred ? ' hidden' : ''}>
      <div class="luxe-reviews__progress" aria-hidden="true"><span></span></div>
      <button type="button" class="luxe-reviews__arrow" data-step="-1" aria-controls="${id}" aria-label="${esc(words.previous)}">${ARROW('M19 12H5M11 6l-6 6 6 6')}</button>
      <button type="button" class="luxe-reviews__arrow" data-step="1" aria-controls="${id}" aria-label="${esc(words.next)}">${ARROW('M5 12h14M13 6l6 6-6 6')}</button>
    </div>`;
  const block = deferred ? held : (source.closest('.shopify-app-block') || source);
  block.before(rail);
  // Judge.me's carousel leaves the page once read, so its script never sets it up: hidden behind the rail, its
  // slider still forced three layouts of about 100 ms on a slow phone, on every page (Core Web Vitals, 05/10).
  // In the theme editor it stays, hidden, so its block can still be selected.
  if (window.Shopify?.designMode) block.setAttribute('data-luxe-replaced', '');
  else block.remove();
  // Use Judge.me's already-enabled native translator on the exact review UUIDs.
  // No review is rewritten in storage. Keep authors/ratings, retain the original
  // text, and expose a translation/original toggle after the vendor returns it.
  const localizeReviewProducts = async () => {
    const handles = [...new Set(reviews.map(review => {
      try {
        const url = new URL(review.href, window.location.origin);
        if (url.origin !== window.location.origin) return '';
        return url.pathname.match(/\/products\/([a-zA-Z0-9][a-zA-Z0-9_-]*)\/?$/)?.[1] || '';
      } catch { return ''; }
    }).filter(Boolean))].slice(0, 20);
    if (!handles.length) return;
    try {
      const root = '/' + String(words.localeRoot || '/').replace(/^\/+|\/+$/g, '');
      const endpoint = new URL((root === '/' ? '' : root) + '/search', window.location.origin);
      endpoint.searchParams.set('view', 'luxe-review-products');
      endpoint.searchParams.set('q', handles.join('|'));
      const response = await fetch(endpoint, {credentials: 'same-origin', headers: {'Accept': 'application/json'}});
      if (!response.ok) return;
      const payload = await response.json();
      if (payload.locale !== document.documentElement.lang || !Array.isArray(payload.products)) return;
      const products = new Map(payload.products.filter(p => handles.includes(p.handle) && typeof p.title === 'string' && p.title.trim()).map(p => [p.handle, p]));
      rail.querySelectorAll('.luxe-review figcaption a').forEach(link => {
        const href = new URL(link.href, window.location.origin);
        const handle = href.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
        const product = products.get(handle);
        if (!product || typeof product.url !== 'string') return;
        const url = new URL(product.url, window.location.origin);
        const expectedPath = (root === '/' ? '' : root) + '/products/' + handle;
        if (url.origin !== window.location.origin || url.pathname !== expectedPath) return;
        link.textContent = product.title;
        link.href = url.pathname + href.search + href.hash;
        link.dataset.productTitleLocalized = 'true';
      });
    } catch { /* Deleted products or unavailable lookup: preserve the authentic caption and link. */ }
  };
  const translateReviews = async () => {
    const settings = window.jdgmSettings || {};
    if (![true, 'true'].includes(settings.widget_translate_review_content_enabled)
        || settings.widget_translate_review_content_method !== 'automatic') return;
    const vendor = window.jdgm;
    if (!vendor) return;
    try {
      if (!window.jdgmCarouselCardAutoTranslate?.autoTranslateCards) {
        const base = new URL(vendor.CDN_BASE_URL || '', window.location.origin);
        if (base.protocol !== 'https:' || !['cdn.shopify.com', 'cdnwidget.judge.me', 'cdn2.judge.me'].includes(base.hostname)) return;
        await import(new URL('carousel_lightbox.js', base).href);
      }
      const translator = window.jdgmCarouselCardAutoTranslate;
      if (!translator?.autoTranslateCards) return;
      const cards = rail.querySelectorAll('.luxe-review');
      cards.forEach((card, index) => {
        const text = card.querySelector('.jdgm-text');
        const original = reviews[index].body;
        let translated = '';
        const observer = new MutationObserver(() => {
          const value = text.textContent.trim();
          if (!value || value === original || value === translated) return;
          translated = value;
          observer.disconnect();
          // The quote remains plain text even when the vendor supplies HTML.
          text.textContent = translated;
          const note = document.createElement('small');
          note.className = 'luxe-review__translation';
          note.textContent = words.automaticTranslation;
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'link luxe-review__translation-toggle';
          toggle.textContent = words.showOriginal;
          toggle.setAttribute('aria-pressed', 'false');
          toggle.addEventListener('click', () => {
            const showingOriginal = toggle.getAttribute('aria-pressed') !== 'true';
            text.textContent = showingOriginal ? original : translated;
            toggle.setAttribute('aria-pressed', String(showingOriginal));
            toggle.textContent = showingOriginal ? words.showTranslation : words.showOriginal;
            note.hidden = showingOriginal;
          });
          card.querySelector('figcaption').append(note, toggle);
          card.dataset.reviewTranslated = 'true';
        });
        observer.observe(text, {childList: true, characterData: true, subtree: true});
      });
      await translator.autoTranslateCards(rail, cards, reviews.map(r => ({uuid: r.id, body: r.body, title: ''})), {designMode: !!window.Shopify?.designMode});
    } catch { /* Vendor unavailable: the authentic original remains visible. */ }
  };
  if (deferred) {
    const open = rail.querySelector('.luxe-reviews__open button');
    open.addEventListener('click', () => {
      const list = rail.querySelector('.luxe-reviews__track');
      list.innerHTML = cardsHtml;
      list.hidden = false;
      rail.querySelector('.luxe-reviews__controls').hidden = false;
      open.closest('.luxe-reviews__open').remove();
      list.focus({ preventScroll: true });
      localizeReviewProducts();
      translateReviews();
      update();
    }, { once: true });
  } else if ('IntersectionObserver' in window) {
    const translationVisibility = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      translationVisibility.disconnect();
      localizeReviewProducts();
      translateReviews();
    }, {rootMargin: '150px'});
    translationVisibility.observe(rail);
  } else setTimeout(() => { localizeReviewProducts(); translateReviews(); }, 0);
  const track = rail.querySelector('.luxe-reviews__track');
  const fill = rail.querySelector('.luxe-reviews__progress span');
  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    const shown = track.clientWidth / (track.scrollWidth || 1);
    fill.style.setProperty('--progress', String(Math.min(1, shown + (max > 0 ? (track.scrollLeft / max) * (1 - shown) : 1 - shown))));
    rail.querySelectorAll('.luxe-reviews__arrow').forEach((button) => {
      button.disabled = button.dataset.step === '-1' ? track.scrollLeft <= 2 : track.scrollLeft >= max - 2;
    });
  };
  track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  addEventListener('resize', update, { passive: true });
  rail.addEventListener('click', (event) => {
    const button = event.target.closest('.luxe-reviews__arrow');
    if (!button) return;
    const card = track.querySelector('.luxe-review');
    const step = (card?.getBoundingClientRect().width || track.clientWidth) + parseFloat(getComputedStyle(track).columnGap || '0');
    track.scrollBy({ left: Number(button.dataset.step) * step, behavior: reduced.matches ? 'auto' : 'smooth' });
  });
  update();
})();

/* ---------- the home's slider titles rise from their masks (owner, 04/10, as in the design: "Nuovi arrivi" and
   "Sport"): each word slides up from under its own edge, 40 ms after the one before, as the title comes on screen.
   A title already on screen at load, or with reduced motion, stays as it is; the words are read once, from a copy
   kept for assistive tech. ---------- */
(() => {
  const titles = [...document.querySelectorAll('.shopify-section--featured-collections .section-stack > .v-stack:first-child > p.h6, .luxe-sport-band h2[id^="luxe-sport-title"] > a')];
  if (!titles.length || reduced.matches) return;
  titles.forEach((title) => {
    const box = title.getBoundingClientRect();
    if (box.top < innerHeight * 0.92 && box.bottom > 0) return;
    const text = title.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;
    const words = text.split(' ').map((word, i) => `<span class="luxe-w"><span style="--i:${i}">${esc(word)}</span></span>`).join(' ');
    title.innerHTML = `<span class="sr-only">${esc(text)}</span><span aria-hidden="true">${words}</span>`;
    title.classList.add('luxe-mask');
    inView(title, () => { title.classList.add('is-in'); }, { margin: '0px 0px -8% 0px' });
  });
})();

/* ---------- a grid card's sizes in stock (snippets/luxe-card-sizes.liquid, owner 27/10): the line keeps whole sizes
   only, wrapping the others out of sight, and here those become «+N», at any card width. Before this runs, or without
   it, the line still shows whole sizes, only without the count. The space is the card's (its info box, whose width the
   grid sets), never the line's own, so hiding sizes cannot resize what is observed; each size is measured once, and a
   pass reads every card before it changes any, so the whole grid costs one layout. ---------- */
if (!customElements.get('luxe-card-sizes')) {
  const lines = new WeakMap(); // a card's info box -> its line
  const measured = new WeakMap(); // a line -> its sizes and their widths
  const resized = new ResizeObserver((entries) => {
    const plans = entries.map(({ target }) => {
      const line = lines.get(target);
      if (!line) return null;
      const style = getComputedStyle(target);
      const space = target.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (space <= 0) return null; // hidden: measured when it shows
      let sizes = measured.get(line);
      if (!sizes) {
        const chips = [...line.querySelectorAll('.luxe-sz:not(.luxe-sz--more)')];
        const more = line.querySelector('.luxe-sz--more');
        sizes = {
          chips,
          more,
          widths: chips.map((chip) => chip.getBoundingClientRect().width),
          moreWidth: more.getBoundingClientRect().width,
          gap: parseFloat(getComputedStyle(line).columnGap) || 0,
        };
        if (!sizes.moreWidth || sizes.widths.some((width) => !width)) return null; // not laid out yet
        measured.set(line, sizes);
      }
      const { widths, moreWidth, gap } = sizes;
      let shown = widths.length;
      if (widths.reduce((sum, width) => sum + width + gap, -gap) > space) {
        let used = moreWidth;
        shown = 0;
        while (shown < widths.length && used + gap + widths[shown] <= space - 1) used += gap + widths[shown++];
      }
      return { sizes, shown };
    });
    plans.forEach((plan) => {
      if (!plan) return;
      const { sizes: { chips, more }, shown } = plan;
      chips.forEach((chip, i) => { chip.hidden = i >= shown; });
      more.hidden = shown === chips.length;
      more.textContent = `+${chips.length - shown}`;
    });
  });
  customElements.define('luxe-card-sizes', class extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector('.luxe-sz--more')) {
        // last, so it wraps out of sight until a pass places it; written with the most digits it can need
        const more = document.createElement('span');
        more.className = 'luxe-sz luxe-sz--more';
        more.textContent = `+${this.children.length - 1}`;
        this.append(more);
      }
      this.box = this.closest('.product-card__info') || this.parentElement;
      lines.set(this.box, this);
      resized.observe(this.box);
    }

    disconnectedCallback() {
      resized.unobserve(this.box);
      lines.delete(this.box);
    }
  });
}

})().catch(error => { setTimeout(() => { throw error; }, 0); });
